/* Progressive enhancement. Real commerce remains Shopify/Appstle-owned. */
(() => {
  'use strict';
  if (window.__EZEATS_CORE_INIT__) return;
  window.__EZEATS_CORE_INIT__ = true;

  const DESIGN_MODE = Boolean(window.Shopify && Shopify.designMode);
  const warnDesign = (...args) => { if (DESIGN_MODE) console.warn('[EZeats]', ...args); };
  // Market/language-aware root (e.g. /en-ca/) for cart AJAX endpoints.
  const ROUTE_ROOT = String((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const cartRoute = path => ROUTE_ROOT + path;

  function announce(message) {
    const status = document.getElementById('ThemeStatus');
    if (status) status.textContent = message;
  }

  function shopHosts() {
    return (document.documentElement.dataset.shopHosts || '')
      .split(',')
      .map(host => host.trim().toLowerCase().replace(/^www\./, ''))
      .filter(Boolean);
  }

  function storefrontUrl(value) {
    const url = new URL(value, location.origin);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (!shopHosts().includes(host)) return url;
    return new URL(url.pathname + url.search + url.hash, location.origin);
  }

  function syncFulfillment(scope) {
    const selected = scope.querySelector('[data-fulfillment-choice]:checked');
    if (!selected) return;
    const pickup = selected.value === 'Pickup';
    scope.querySelectorAll('[data-delivery-fields]').forEach(group => {
      group.hidden = pickup;
      group.querySelectorAll('input,select').forEach(field => {
        if (field.matches('[data-canada-address]')) field.required = !pickup;
        field.disabled = pickup;
      });
    });
    scope.querySelectorAll('[data-pickup-fields]').forEach(group => {
      group.hidden = !pickup;
      group.querySelectorAll('select').forEach(field => {
        field.disabled = !pickup;
        field.required = pickup;
      });
    });
  }

  function initFulfillment(scope) {
    const owner = scope.matches('form') ? scope : scope.querySelector('form') || scope;
    if (!owner.querySelector('[data-fulfillment-choice]')) return;
    owner.querySelectorAll('[data-fulfillment-choice]').forEach(input => {
      if (!input.dataset.fulfillmentReady) {
        input.dataset.fulfillmentReady = 'true';
        input.addEventListener('change', () => syncFulfillment(owner));
      }
    });
    syncFulfillment(owner);
  }

  function addressComponent(place, type, shortName) {
    const component = (place.addressComponents || place.address_components || []).find(item => {
      const types = item.types || [];
      return types.includes(type);
    });
    return component ? (shortName ? (component.shortText || component.short_name) : (component.longText || component.long_name)) || '' : '';
  }

  function applyPlace(form, place) {
    const streetNumber = addressComponent(place, 'street_number');
    const route = addressComponent(place, 'route');
    const values = {
      address1: [streetNumber, route].filter(Boolean).join(' '),
      city: addressComponent(place, 'locality') || addressComponent(place, 'postal_town') || addressComponent(place, 'administrative_area_level_3'),
      province: addressComponent(place, 'administrative_area_level_1', true),
      zip: addressComponent(place, 'postal_code'),
      country: addressComponent(place, 'country') || 'Canada'
    };
    Object.entries(values).forEach(([part, value]) => {
      const field = form.querySelector('[data-address-part="' + part + '"]');
      if (field) field.value = value;
    });
    const visible = form.querySelector('[data-canada-address]');
    const formatted = place.formattedAddress || place.formatted_address;
    if (visible && formatted) visible.value = formatted;
  }

  async function initPlaces(root) {
    if (!window.google || !google.maps || !google.maps.places) return;
    root.querySelectorAll('[data-canada-address]').forEach(input => {
      if (input.dataset.placesReady) return;
      input.dataset.placesReady = 'true';
      const form = input.closest('form');
      if (!form) return;
      try {
        if (google.maps.places.PlaceAutocompleteElement) {
          const element = new google.maps.places.PlaceAutocompleteElement({includedRegionCodes:['ca']});
          element.className = 'ezeats-place-autocomplete';
          element.setAttribute('placeholder', input.placeholder || 'Enter your delivery address');
          input.hidden = true;
          input.parentNode.appendChild(element);
          element.addEventListener('gmp-select', async event => {
            const place = event.placePrediction.toPlace();
            await place.fetchFields({fields:['formattedAddress','addressComponents']});
            applyPlace(form, place);
          });
        } else if (google.maps.places.Autocomplete) {
          const autocomplete = new google.maps.places.Autocomplete(input, {types:['address'], componentRestrictions:{country:'ca'}});
          autocomplete.addListener('place_changed', () => applyPlace(form, autocomplete.getPlace()));
        }
      } catch (error) {
        input.hidden = false;
        warnDesign('Address autocomplete could not start.', error);
      }
    });
  }

  window.EZeatsPlacesReady = () => initPlaces(document);
  const dateParts = (now, timeZone) => Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone, year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', hourCycle:'h23'
  }).formatToParts(now).filter(p => p.type !== 'literal').map(p => [p.type,p.value]));
  function earliestDate(now, timeZone, cutoff) {
    const p = dateParts(now,timeZone);
    const day = new Date(Date.UTC(Number(p.year),Number(p.month)-1,Number(p.day)));
    if(Number(p.hour) >= Number(cutoff)) day.setUTCDate(day.getUTCDate()+1);
    return day.toISOString().slice(0,10);
  }
  function refreshDate(field) {
    const input = field.querySelector('[data-order-date]');
    try {
      const minimum = earliestDate(new Date(), field.dataset.timezone, field.dataset.cutoff);
      input.min = minimum;
      input.setCustomValidity(input.value && input.value < minimum ? 'Please select '+minimum+' or a later date.' : '');
      field.querySelector('[data-date-help]').textContent = 'Earliest requested date: '+minimum+'. Dates use '+field.dataset.timezone+' time. Final availability and delivery method are confirmed separately.';
    } catch {
      input.setCustomValidity('Date configuration is unavailable. Please contact the store.');
      field.querySelector('[data-date-help]').textContent = 'Date configuration needs attention. Please contact the store.';
    }
  }

  function pickupMeta(form) {
    const select = form.querySelector('[data-pickup-select]');
    if (!select || !select.value) return { id: '', name: '' };
    const option = select.options[select.selectedIndex];
    return {
      id: (option && option.dataset.pickupId) || select.value,
      name: select.value
    };
  }

  function buildFulfillmentAttributes(form, base) {
    const attributes = Object.assign({}, base || {});
    const orderType = attributes['Requested order type'] || attributes['Order Type'] || '';
    attributes['Order Type'] = orderType;
    attributes['Requested order type'] = orderType;

    if (orderType === 'Pickup') {
      const meta = pickupMeta(form);
      attributes['Pickup Location'] = meta.name;
      attributes['Pickup Location ID'] = meta.id;
      attributes['Pickup branch'] = meta.name;
      attributes['Delivery Address'] = '';
      attributes['Delivery address'] = '';
      attributes['Delivery first name'] = '';
      attributes['Delivery last name'] = '';
      attributes['Delivery address 1'] = '';
      attributes['Delivery address 2'] = '';
      attributes['Delivery city'] = '';
      attributes['Delivery province'] = '';
      attributes['Delivery postal code'] = '';
      attributes['Delivery country'] = '';
    } else if (orderType === 'Delivery') {
      attributes['Pickup Location'] = '';
      attributes['Pickup Location ID'] = '';
      attributes['Pickup branch'] = '';
      if (attributes['Delivery address']) attributes['Delivery Address'] = attributes['Delivery address'];
    }
    return attributes;
  }

  function showPickupCheckoutNotice(locationName) {
    return new Promise(resolve => {
      const existing = document.querySelector('[data-pickup-checkout-modal]');
      if (existing) existing.remove();
      const overlay = document.createElement('div');
      overlay.className = 'pickup-checkout-modal';
      overlay.setAttribute('data-pickup-checkout-modal', '');
      overlay.innerHTML =
        '<div class="pickup-checkout-modal__panel" role="dialog" aria-modal="true" aria-labelledby="PickupCheckoutTitle">' +
          '<h2 id="PickupCheckoutTitle">Confirm pickup</h2>' +
          '<p>Pickup selected at <strong data-pickup-name></strong>. On the next screen, choose <strong>Pickup</strong> to confirm this order type.</p>' +
          '<div class="pickup-checkout-modal__actions">' +
            '<button type="button" class="button button--outline" data-pickup-cancel>Go back</button>' +
            '<button type="button" class="button button--yellow" data-pickup-continue>Continue to checkout</button>' +
          '</div>' +
        '</div>';
      overlay.querySelector('[data-pickup-name]').textContent = String(locationName || 'your chosen location');
      document.body.appendChild(overlay);
      const previousFocus = document.activeElement;
      const cancelButton = overlay.querySelector('[data-pickup-cancel]');
      const continueButton = overlay.querySelector('[data-pickup-continue]');
      const onKeydown = event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          cleanup(false);
        } else if (event.key === 'Tab') {
          // Keep focus inside the dialog (only two focusable buttons).
          const target = event.shiftKey ? cancelButton : continueButton;
          if (document.activeElement === target || !overlay.contains(document.activeElement)) {
            event.preventDefault();
            (event.shiftKey ? continueButton : cancelButton).focus();
          }
        }
      };
      const cleanup = (ok) => {
        document.removeEventListener('keydown', onKeydown, true);
        overlay.remove();
        if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
        resolve(ok);
      };
      document.addEventListener('keydown', onKeydown, true);
      cancelButton.addEventListener('click', () => cleanup(false));
      continueButton.addEventListener('click', () => cleanup(true));
      overlay.addEventListener('click', event => { if (event.target === overlay) cleanup(false); });
      continueButton.focus();
    });
  }

  const APP_NOTE_RE = /_apdData\s*:/;

  function isAppOwnedNote(value) {
    return APP_NOTE_RE.test(String(value || ''));
  }

  function concealAppNoteField(field) {
    if (!field) return;
    const wrap = field.closest('[data-cart-note]');
    if (wrap) wrap.classList.add('cart-note--hidden');
    else field.classList.add('cart-note--hidden');
    const label = field.id ? document.querySelector('label[for="' + CSS.escape(field.id) + '"]') : (wrap ? wrap.querySelector('label') : null);
    if (label) label.setAttribute('hidden', '');
    field.setAttribute('aria-hidden', 'true');
    field.tabIndex = -1;
  }

  function hideAppOwnedCartNotes(root) {
    (root.querySelectorAll ? root : document).querySelectorAll('textarea[name="note"], input[name="note"]').forEach(field => {
      const apply = () => {
        if (isAppOwnedNote(field.value)) concealAppNoteField(field);
      };
      apply();
      if (field.dataset.appNoteWatch === 'true') return;
      field.dataset.appNoteWatch = 'true';
      field.addEventListener('input', apply);
      field.addEventListener('change', apply);
    });
  }

  function findScheduleTimezone(form) {
    let match = null;
    form.querySelectorAll('p, div, span, small, label').forEach(el => {
      if (el.closest('[data-cart-delivery-note]')) return;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/schedule time zone/i.test(text) || text.length > 160) return;
      if (!match || text.length < (match.textContent || '').replace(/\s+/g, ' ').trim().length) match = el;
    });
    return match;
  }

  function placeCartDeliveryNote(root) {
    const scope = root && root.querySelector ? root : document;
    const form = scope.querySelector('[data-cart-form]') || document.querySelector('[data-cart-form]');
    if (!form) return;
    const note = form.querySelector('[data-cart-delivery-note]');
    if (!note) return;
    const checkout = form.querySelector('[name="checkout"]');
    const home = checkout ? checkout.parentNode : note.parentNode;
    if (!home) return;
    // Keep the note outside app-rendered markup. Inserting it inside the
    // pickup/delivery widget made the app re-render and drop it, and moving it
    // back each time made the cart shake. Place it after the widget's top-level
    // block instead, which lands right under the schedule time zone line.
    let anchor = findScheduleTimezone(form);
    while (anchor && anchor.parentNode !== home) anchor = anchor.parentNode;
    if (anchor && anchor !== note) {
      if (anchor.nextElementSibling !== note) home.insertBefore(note, anchor.nextSibling);
      return;
    }
    if (checkout && note.nextElementSibling !== checkout) home.insertBefore(note, checkout);
  }

  function observeAppOwnedCartNotes(root) {
    hideAppOwnedCartNotes(root);
    placeCartDeliveryNote(root);
    const scope = root && root.querySelector ? root : document;
    const form = scope.querySelector('[data-cart-form]');
    if (!form || form.dataset.appNoteObserver === 'true') return;
    form.dataset.appNoteObserver = 'true';
    // Batch bursts of app re-renders into one pass per frame.
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        hideAppOwnedCartNotes(form);
        placeCartDeliveryNote(form);
      });
    });
    observer.observe(form, { childList: true, subtree: true });
    [0, 400, 1200, 3000].forEach(delay => {
      window.setTimeout(() => {
        hideAppOwnedCartNotes(form);
        placeCartDeliveryNote(form);
      }, delay);
    });
  }

  function initialize(root) {
    observeAppOwnedCartNotes(root);
    root.querySelectorAll('[data-order-intro], [data-box-form]').forEach(initFulfillment);
    if (window.google) initPlaces(root);
    root.querySelectorAll('[data-order-fields]').forEach(field => {
      if(field.dataset.initialized) return;
      field.dataset.initialized='true';
      refreshDate(field);
      field.addEventListener('input',()=>refreshDate(field));
      field.addEventListener('focusin',()=>refreshDate(field));
      field.closest('form')?.addEventListener('submit',e=>{
        refreshDate(field);
        if(!field.querySelector('input').reportValidity()) e.preventDefault();
      });
    });
    root.querySelectorAll('[data-gallery]').forEach(gallery=>{
      if(gallery.dataset.initialized) return;
      gallery.dataset.initialized='true';
      const main=gallery.querySelector('.gallery__main');
      const thumbs=gallery.querySelectorAll('[data-gallery-thumb]');
      if(!main||!thumbs.length) return;
      thumbs.forEach(thumb=>{
        thumb.addEventListener('click',()=>{
          const full=thumb.dataset.full;
          const img=thumb.querySelector('img');
          if(full && main.tagName==='IMG'){
            main.src=full;
            if(img) main.alt=img.alt;
          }
          thumbs.forEach(t=>t.setAttribute('aria-selected','false'));
          thumb.setAttribute('aria-selected','true');
        });
      });
    });
    root.querySelectorAll('[data-variant-select]').forEach(select=>{
      if(select.dataset.initialized) return;
      select.dataset.initialized='true';
      select.addEventListener('change',()=>select.form.requestSubmit());
    });
    root.querySelectorAll('[data-order-intro]').forEach(form=>{
      if(form.dataset.initialized) return;
      form.dataset.initialized='true';
      form.addEventListener('submit',async e=>{
        e.preventDefault();
        form.querySelectorAll('[data-order-fields]').forEach(refreshDate);
        if(!form.reportValidity()) return;
        const error=form.querySelector('[data-order-error]');
        const button=form.querySelector('button[type=submit]');
        button.disabled=true;
        error.textContent='';
        try{
          // Items already in the cart: finish that order instead of starting a new one.
          try{
            const cartResponse=await fetch(form.dataset.root+'cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
            if(cartResponse.ok && (await cartResponse.json()).item_count>0){
              location.assign(form.dataset.root+'cart');
              return;
            }
          }catch(_){}
          const raw={};
          for(const [key,value] of new FormData(form).entries()){
            const match=key.match(/^attributes\[(.+)\]$/);
            if(match) raw[match[1]]=value;
          }
          const attributes=buildFulfillmentAttributes(form, raw);
          if (window.EZeatsBoxState) {
            window.EZeatsBoxState.update({
              orderType: attributes['Order Type'] || '',
              pickupLocationId: attributes['Pickup Location ID'] || '',
              pickupLocationName: attributes['Pickup Location'] || '',
              deliveryAddress: attributes['Delivery Address'] || attributes['Delivery address'] || ''
            });
          }
          const response=await fetch(form.dataset.root+'cart/update.js',{
            method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},
            body:JSON.stringify({attributes})
          });
          if(!response.ok) throw new Error('Your preferences could not be saved. Please try again.');
          if (form.hasAttribute('data-open-box-after-save')) {
            document.dispatchEvent(new CustomEvent('box:open', { detail: { attributes } }));
            button.disabled=false;
            return;
          }
          const destination=storefrontUrl(form.dataset.destination);
          if(!['https:','http:'].includes(destination.protocol)) throw new Error('The meal builder link is not configured correctly.');
          location.assign(destination.href);
        }catch(err){
          error.textContent=err.message||'Something went wrong. Please try again.';
          button.disabled=false;
        }
      });
    });
    root.querySelectorAll('[data-testimonial-slider]').forEach(initTestimonialSlider);
  }

  function testimonialPerView() {
    if (window.matchMedia('(max-width:749px)').matches) return 1;
    if (window.matchMedia('(max-width:990px)').matches) return 2;
    return 3;
  }

  function prefersReducedMotion() {
    return document.body.classList.contains('animations-disabled') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initTestimonialSlider(slider) {
    if (!slider || slider.dataset.initialized === 'true') return;
    const track = slider.querySelector('.testimonial-slider__track');
    const slides = track ? Array.from(track.children) : [];
    if (!track || slides.length === 0) return;
    slider.dataset.initialized = 'true';

    const prev = slider.querySelector('[data-slider-prev]');
    const next = slider.querySelector('[data-slider-next]');
    const dotsWrap = slider.querySelector('[data-slider-dots]');
    const autoplay = slider.dataset.autoplay === 'true';
    const intervalMs = Math.max(3000, Number(slider.dataset.interval) || 5000);
    let index = 0;
    let timer = null;
    let pointerStart = null;

    function maxIndex(perView) {
      return Math.max(0, slides.length - perView);
    }

    function offsetFor(i) {
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const width = slides[0].getBoundingClientRect().width;
      return i * (width + gap);
    }

    function renderDots(count) {
      if (!dotsWrap) return;
      if (dotsWrap.childElementCount !== count) {
        dotsWrap.innerHTML = '';
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'testimonial-slider__dot';
          dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
          dot.addEventListener('click', () => goTo(i, true));
          dotsWrap.appendChild(dot);
        }
      }
      Array.from(dotsWrap.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function goTo(nextIndex, animate) {
      const perView = testimonialPerView();
      const last = maxIndex(perView);
      slider.classList.toggle('is-static', last === 0);
      if (last === 0) {
        index = 0;
        track.style.transition = 'none';
        track.style.transform = 'translate3d(0,0,0)';
        renderDots(1);
        stop();
        return;
      }
      index = ((nextIndex % (last + 1)) + (last + 1)) % (last + 1);
      track.style.transition = animate && !prefersReducedMotion() ? 'transform .45s ease' : 'none';
      track.style.transform = 'translate3d(' + (-offsetFor(index)) + 'px,0,0)';
      renderDots(last + 1);
    }

    function play() {
      stop();
      if (!autoplay || prefersReducedMotion() || maxIndex(testimonialPerView()) === 0) return;
      timer = window.setInterval(() => goTo(index + 1, true), intervalMs);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    prev?.addEventListener('click', () => { goTo(index - 1, true); play(); });
    next?.addEventListener('click', () => { goTo(index + 1, true); play(); });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', play);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', event => {
      if (!slider.contains(event.relatedTarget)) play();
    });

    track.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerStart = event.clientX;
      track.setPointerCapture(event.pointerId);
    });
    track.addEventListener('pointerup', event => {
      if (pointerStart == null) return;
      const delta = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(delta) < 40) return;
      goTo(index + (delta < 0 ? 1 : -1), true);
      play();
    });
    track.addEventListener('pointercancel', () => { pointerStart = null; });

    slider.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(index + 1, true); play(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(index - 1, true); play(); }
    });

    slider._testimonialGoTo = (slideIndex) => {
      const perView = testimonialPerView();
      goTo(Math.min(slideIndex, maxIndex(perView)), true);
      play();
    };
    slider._testimonialDestroy = stop;

    const resize = new ResizeObserver(() => goTo(index, false));
    resize.observe(slider);
    slider._testimonialResize = resize;

    goTo(0, false);
    play();
  }

  function pinFloatingCta() {
    const cta = document.querySelector('.ezeats-floating-cta');
    const footer = document.querySelector('.ezeats-footer');
    if (!cta || !footer || cta.dataset.pinReady) return;
    cta.dataset.pinReady = 'true';
    const gap = 24;
    const update = () => {
      const footerTop = footer.getBoundingClientRect().top;
      if (footerTop < window.innerHeight - gap) {
        cta.style.bottom = (window.innerHeight - footerTop + gap) + 'px';
      } else {
        cta.style.bottom = '';
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  document.addEventListener('DOMContentLoaded',()=>{
    initialize(document);
    pinFloatingCta();
  });
  document.addEventListener('shopify:section:load',event=>initialize(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    event.target.querySelectorAll('[data-testimonial-slider]').forEach(slider=>{
      slider._testimonialDestroy?.();
      slider._testimonialResize?.disconnect();
    });
  });
  document.addEventListener('shopify:block:select',event=>{
    const slider = event.target.closest('[data-testimonial-slider]');
    if (!slider || !slider._testimonialGoTo) return;
    const slides = Array.from(slider.querySelectorAll('.testimonial-card'));
    const index = slides.indexOf(event.target);
    if (index >= 0) slider._testimonialGoTo(index);
  });
  document.addEventListener('keydown',event=>{
    if(event.key==='Escape') document.querySelectorAll('.mobile-nav[open],.nav-dropdown[open],.filters details[open]').forEach(d=>{d.open=false;d.querySelector('summary')?.focus();});
  });
  document.addEventListener('toggle',event=>{
    const nav=event.target;
    if(!nav.classList||!nav.classList.contains('mobile-nav')) return;
    document.documentElement.classList.toggle('nav-open',nav.open);
  },true);
  document.addEventListener('shopify:section:unload',()=>{
    if(!document.querySelector('.mobile-nav[open]')) document.documentElement.classList.remove('nav-open');
  });
  document.addEventListener('click',event=>{
    const closer=event.target.closest('[data-mobile-nav-close]');
    if(closer){
      const nav=closer.closest('.mobile-nav');
      if(!nav) return;
      nav.open=false;
      nav.querySelector('summary')?.focus();
      return;
    }
    if(!document.documentElement.classList.contains('nav-open')) return;
    if(event.target.closest('.mobile-drawer,.mobile-nav>summary,.header-actions')) return;
    document.querySelectorAll('.mobile-nav[open]').forEach(nav=>{nav.open=false;});
  });
  document.addEventListener('visibilitychange',()=>{
    if(!document.hidden) document.querySelectorAll('[data-order-fields]').forEach(refreshDate);
  });

  document.addEventListener('click', event => {
    const stepButton = event.target.closest('[data-qty]');
    if (!stepButton) return;
    const input = stepButton.closest('.cart-qty')?.querySelector('input[data-line-key]');
    if (!input || input.disabled) return;
    const min = input.min === '' ? 0 : Number(input.min);
    const next = Math.max(min, (Number(input.value) || 0) + (Number(stepButton.dataset.qty) || 0));
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    announce(next === 0
      ? 'Quantity set to 0. This item will be removed when you update the cart or continue to checkout.'
      : 'Quantity set to ' + next + '. Saved when you update the cart or continue to checkout.');
  });

  // The cart note is rendered at page load. Pickup/delivery apps may save a newer
  // note via AJAX afterwards, so only submit the note when it was edited on this page;
  // otherwise the stale copy would overwrite the app's data.
  function cartNoteField(form) {
    return form.querySelector('textarea[name="note"], input[name="note"]');
  }
  function noteEditedOnPage(field) {
    return Boolean(field) && field.value !== field.defaultValue;
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches || !form.matches('[data-cart-form]')) return;
    // Form-level handlers (date validation) run before this document listener.
    if (event.defaultPrevented) return;
    const note = cartNoteField(form);
    if (note && !noteEditedOnPage(note)) note.disabled = true;
  });

  async function saveCartForm(form) {
    const body = { updates: {} };
    let changed = false;
    form.querySelectorAll('input[data-line-key]').forEach(input => {
      const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
      body.updates[input.dataset.lineKey] = quantity;
      if (String(quantity) !== input.defaultValue) changed = true;
    });
    const note = cartNoteField(form);
    if (note && !note.disabled && noteEditedOnPage(note)) {
      body.note = note.value;
      changed = true;
    }
    const attributes = {};
    for (const [key, value] of new FormData(form).entries()) {
      const match = key.match(/^attributes\[(.+)\]$/);
      if (match) attributes[match[1]] = value;
    }
    if (Object.keys(attributes).length) body.attributes = attributes;

    const response = changed || body.attributes
      ? await fetch(cartRoute('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        })
      : await fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be saved.');
    return { cart: await response.json(), changed };
  }

  document.addEventListener('click', async event => {
    const checkout = event.target.closest('[data-checkout-prefill]');
    if (!checkout) return;
    const form = checkout.closest('form');
    if (!form || form.dataset.prefillReady === 'true') return;
    event.preventDefault();
    form.querySelectorAll('[data-order-fields]').forEach(refreshDate);
    if (!form.reportValidity()) return;
    checkout.disabled = true;
    try {
      // Persist quantity, note and attribute edits before leaving the cart page.
      const { cart, changed } = await saveCartForm(form);
      if (!cart.item_count) {
        location.assign(cartRoute('cart'));
        return;
      }
      const attrs = cart.attributes || {};
      const orderType = attrs['Order Type'] || attrs['Requested order type'] || '';
      const checkoutUrl = new URL('/checkout', location.origin);
      if (orderType === 'Delivery') {
        const fields = {
          first_name: attrs['Delivery first name'], last_name: attrs['Delivery last name'],
          address1: attrs['Delivery address 1'] || attrs['Delivery Address'] || attrs['Delivery address'],
          address2: attrs['Delivery address 2'],
          city: attrs['Delivery city'], province: attrs['Delivery province'],
          zip: attrs['Delivery postal code'], country: attrs['Delivery country'] || 'Canada'
        };
        Object.entries(fields).forEach(([key,value]) => {
          if (value) checkoutUrl.searchParams.set('checkout[shipping_address][' + key + ']', value);
        });
      } else if (orderType === 'Pickup') {
        const locationName = attrs['Pickup Location'] || attrs['Pickup branch'] || '';
        if (DESIGN_MODE) {
          warnDesign('Pickup cannot be auto-selected in Shopify Checkout from theme JS. Showing confirmation instead.');
        }
        const ok = await showPickupCheckoutNotice(locationName);
        if (!ok) {
          // The cart was already saved; reload so the page shows the saved totals.
          if (changed) location.reload();
          else checkout.disabled = false;
          return;
        }
      }
      form.dataset.prefillReady = 'true';
      location.assign(checkoutUrl.href);
    } catch (error) {
      warnDesign('Checkout prefill failed; falling back to the cart form.', error);
      form.dataset.prefillReady = 'true';
      checkout.disabled = false;
      form.requestSubmit(checkout);
    }
  });
  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-read-more-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('[data-read-more]');
    const short = wrap?.querySelector('[data-read-more-short]');
    const full = wrap?.querySelector('[data-read-more-full]');
    if (!short || !full) return;
    const expand = full.hidden;
    full.hidden = !expand;
    short.hidden = expand;
    toggle.textContent = expand ? 'Read less' : 'Read more';
    toggle.setAttribute('aria-expanded', String(expand));
  });

  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-empty-cart]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    try {
      const response = await fetch(cartRoute('cart/clear.js'), { method: 'POST', headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be emptied.');
      // The box builder (Appstle) remembers picks in browser storage, so wipe those too.
      [window.localStorage, window.sessionStorage].forEach(store => {
        try {
          Object.keys(store).filter(key => /appstle|(^|[-_.])bb([-_.]|$)|ezeats\.box/i.test(key)).forEach(key => store.removeItem(key));
        } catch (_) {}
      });
      location.reload();
    } catch (error) {
      warnDesign(error);
      button.disabled = false;
    }
  });

  // A box split across several lines is removed together, so a partial box
  // (below the builder's minimum) can never reach checkout.
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-remove-keys]');
    if (!link) return;
    const keys = link.dataset.removeKeys.split(',').filter(Boolean);
    if (keys.length < 2) return;
    event.preventDefault();
    try {
      const updates = Object.fromEntries(keys.map(key => [key, 0]));
      const response = await fetch(cartRoute('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Box could not be removed.');
      location.reload();
    } catch (error) {
      warnDesign(error);
      location.assign(link.href);
    }
  });

  // Same back-button case for the order-preferences form, which disables its
  // button before redirecting to the meal builder.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-order-intro] button[type="submit"]').forEach(button => { button.disabled = false; });
  });

  // Returning via the Back button restores a frozen page (disabled checkout button,
  // disabled note, stale quantities). Reload the cart so it reflects the server.
  window.addEventListener('pageshow', event => {
    if (event.persisted && document.querySelector('[data-cart-form]')) location.reload();
  });
  if(typeof module!=='undefined'&&module.exports) module.exports={earliestDate};
})();



/* ===================== Build-a-box state + Appstle handoff ===================== */
(() => {
  'use strict';
  if (window.__EZEATS_BOX_INIT__) return;
  window.__EZEATS_BOX_INIT__ = true;

  const STATE_KEY = 'ezeats.boxState.v1';
  const DESIGN_MODE = Boolean(window.Shopify && Shopify.designMode);
  const normalize = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const digits = value => String(value || '').replace(/\D+/g, '');
  const warnDesign = (...args) => { if (DESIGN_MODE) console.warn('[EZeats Box]', ...args); };

  // Themed replacement for window.alert (sits above Appstle's cart drawer).
  function showModal(message, title) {
    const existing = document.querySelector('[data-ezeats-modal]');
    if (existing) existing.remove();
    const previous = document.activeElement;
    const overlay = document.createElement('div');
    overlay.className = 'ezeats-modal';
    overlay.setAttribute('data-ezeats-modal', '');
    overlay.innerHTML =
      '<div class="ezeats-modal__panel" role="alertdialog" aria-modal="true" aria-labelledby="ezeats-modal-title" aria-describedby="ezeats-modal-text">' +
        '<h2 class="ezeats-modal__title" id="ezeats-modal-title"></h2>' +
        '<p class="ezeats-modal__text" id="ezeats-modal-text"></p>' +
        '<button type="button" class="ezeats-modal__btn">OK</button>' +
      '</div>';
    overlay.querySelector('.ezeats-modal__title').textContent = title || 'Almost there';
    overlay.querySelector('.ezeats-modal__text').textContent = message;
    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey, true);
      if (previous && previous.focus) previous.focus();
    };
    const onKey = event => {
      if (event.key === 'Escape') { event.stopPropagation(); close(); }
      else if (event.key === 'Tab') { event.preventDefault(); overlay.querySelector('button').focus(); }
    };
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.querySelector('button').addEventListener('click', close);
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(overlay);
    overlay.querySelector('button').focus();
  }
  const ROUTE_ROOT = String((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const cartRoute = path => ROUTE_ROOT + path;

  function shopHosts() {
    return (document.documentElement.dataset.shopHosts || '')
      .split(',')
      .map(host => host.trim().toLowerCase().replace(/^www\./, ''))
      .filter(Boolean);
  }

  function storefrontUrl(value) {
    const url = new URL(value, location.origin);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (!shopHosts().includes(host)) return url;
    return new URL(url.pathname + url.search + url.hash, location.origin);
  }

  function emptyState() {
    return {
      frequencyKey: '',
      frequencyLabel: '',
      sellingPlanId: '',
      appstleFrequency: '',
      orderType: '',
      pickupLocationId: '',
      pickupLocationName: '',
      deliveryAddress: '',
      requestedDate: '',
      meals: '',
      planProductId: '',
      selectedProducts: {}
    };
  }

  function readState() {
    try {
      const raw = sessionStorage.getItem(STATE_KEY);
      if (!raw) return emptyState();
      return Object.assign(emptyState(), JSON.parse(raw));
    } catch {
      return emptyState();
    }
  }

  function writeState(next) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(next)); } catch (error) {
      warnDesign('Could not persist box state.', error);
    }
    window.EZeatsBoxState.current = next;
    return next;
  }

  function updateState(partial) {
    return writeState(Object.assign(readState(), partial || {}));
  }

  window.EZeatsBoxState = {
    current: readState(),
    read: readState,
    update: updateState,
    clearAfterCheckout() {
      try { sessionStorage.removeItem(STATE_KEY); } catch (_) {}
      this.current = emptyState();
    }
  };

  function earliestDate(now, timeZone, cutoff) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone, year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', hourCycle:'h23'
    }).formatToParts(now).filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
    const day = new Date(Date.UTC(Number(parts.year), Number(parts.month)-1, Number(parts.day)));
    if (Number(parts.hour) >= Number(cutoff)) day.setUTCDate(day.getUTCDate()+1);
    return day.toISOString().slice(0,10);
  }

  function parsePlanMap(modal) {
    const fromInputs = () => Array.from(modal.querySelectorAll('input[data-selling-plan]')).map(input => ({
      frequencyKey: input.dataset.frequencyKey || '',
      frequencyLabel: input.value || '',
      appstleFrequency: input.dataset.appstleFrequency || input.value || '',
      sellingPlanId: digits(input.dataset.sellingPlan)
    })).filter(item => item.sellingPlanId);

    try {
      const raw = modal.getAttribute('data-plan-map') || '[]';
      const list = JSON.parse(raw);
      const parsed = Array.isArray(list) ? list.filter(item => item && item.sellingPlanId) : [];
      return parsed.length ? parsed : fromInputs();
    } catch (error) {
      warnDesign('Invalid plan map JSON.', error);
      return fromInputs();
    }
  }

  function planById(plans, sellingPlanId) {
    const id = digits(sellingPlanId);
    if (!id) return null;
    return plans.find(plan => digits(plan.sellingPlanId) === id) || null;
  }

  function planFromLabel(text) {
    return plans.find(plan =>
      exactFrequencyMatch(text, plan.appstleFrequency) || exactFrequencyMatch(text, plan.frequencyLabel)
    ) || null;
  }

  // Reads the frequency currently shown in Appstle's UI. Returns null when it
  // cannot be read — never a default, so Weekly is not chosen silently.
  function planFromVisibleFrequency(app) {
    const root = app || document.querySelector('#root .appstle_bundle');
    if (!root || !plans.length) return null;
    const selects = frequencySelects(root, plans);
    if (selects.length) {
      for (const select of selects) {
        const option = select.options[select.selectedIndex];
        if (!option) continue;
        const matched = planById(plans, option.value) || plans.find(plan =>
          exactFrequencyMatch(option.textContent, plan.appstleFrequency) ||
          exactFrequencyMatch(option.textContent, plan.frequencyLabel)
        );
        if (matched && digits(matched.sellingPlanId)) return matched;
      }
      return null;
    }
    const trigger = root.querySelector('button[aria-haspopup="listbox"]');
    if (trigger) return planFromLabel(trigger.textContent);
    return null;
  }

  // True when the shopper picked Appstle's "One Time Purchase" option (no selling plan).
  function isOneTimeSelected() {
    // The cart drawer's frequency picker is a portal outside #root, so search the whole document.
    const oneTime = /one[\s-]*time/i;
    const trigger = document.querySelector('.as-plan-select-btn, button[aria-haspopup="listbox"]');
    if (trigger && oneTime.test(trigger.textContent || '')) return true;
    for (const select of document.querySelectorAll('#root select, .as-plan-select-box select')) {
      const option = select.options[select.selectedIndex];
      if (option && oneTime.test(option.textContent) && !planById(plans, option.value)) return true;
    }
    return false;
  }

  function rememberPlan(plan) {
    if (!plan || !digits(plan.sellingPlanId)) return;
    if (digits(readState().sellingPlanId) === digits(plan.sellingPlanId)) return;
    updateState({
      sellingPlanId: digits(plan.sellingPlanId),
      frequencyKey: plan.frequencyKey || '',
      frequencyLabel: plan.frequencyLabel || '',
      appstleFrequency: plan.appstleFrequency || plan.frequencyLabel || ''
    });
    syncCartAttributesFromState().catch(error => warnDesign('Could not sync frequency cart attributes.', error));
  }

  // Appstle's frequency picker is a custom listbox, not a <select>, so option
  // clicks are the only signal that the shopper changed frequency.
  // Appstle's drawer defaults to Weekly. That default is never treated as a choice:
  // the shopper must pick a frequency (or arrive with one from the order page).
  let frequencyChosen = false;
  function markFrequencyChosen() {
    frequencyChosen = true;
    document.body.classList.add('ezeats-freq-chosen');
  }
  document.addEventListener('click', event => {
    const option = event.target.closest && event.target.closest('#root .appstle_bundle [role="option"], .as-plan-select-box [role="option"], [id^="headlessui-listbox-options"] [role="option"]');
    if (!option) return;
    markFrequencyChosen();
    if (/one[\s-]*time/i.test(option.textContent || '')) {
      updateState({ sellingPlanId: '', frequencyKey: '', frequencyLabel: '', appstleFrequency: '' });
      return;
    }
    rememberPlan(planFromLabel(option.textContent));
  }, true);

  // What the shopper sees in Appstle wins over the saved choice.
  function ensureSellingPlan(app) {
    rememberPlan(planFromVisibleFrequency(app));
    return readState();
  }

  function planByKey(plans, frequencyKey) {
    const key = normalize(frequencyKey);
    if (!key) return null;
    return plans.find(plan => normalize(plan.frequencyKey) === key) || null;
  }

  function exactFrequencyMatch(candidate, target) {
    const a = normalize(candidate);
    const b = normalize(target);
    if (!a || !b) return false;
    return a === b;
  }

  function findSellingPlanOption(select, sellingPlanId, frequencyLabel, appstleFrequency) {
    const id = digits(sellingPlanId);
    const options = Array.from(select.options || []);
    if (id) {
      const byValue = options.find(option => digits(option.value) === id);
      if (byValue) return byValue;
      const byData = options.find(option => digits(option.dataset.sellingPlan || option.getAttribute('data-selling-plan') || '') === id);
      if (byData) return byData;
      const byTextId = options.find(option => digits(option.textContent) === id || normalize(option.textContent).includes(id));
      if (byTextId && options.filter(option => digits(option.value)).length === 0) return byTextId;
    }
    const labels = [appstleFrequency, frequencyLabel].filter(Boolean);
    for (const label of labels) {
      const exact = options.find(option => exactFrequencyMatch(option.textContent, label) || exactFrequencyMatch(option.value, label));
      if (exact) return exact;
    }
    return null;
  }

  function frequencySelects(app, planList) {
    return Array.from(app.querySelectorAll('select')).filter(select => {
      const options = Array.from(select.options || []);
      const matches = (planList || []).filter(plan => options.some(option =>
        digits(option.value) === digits(plan.sellingPlanId) ||
        exactFrequencyMatch(option.textContent, plan.appstleFrequency) ||
        exactFrequencyMatch(option.textContent, plan.frequencyLabel)
      ));
      return matches.length >= Math.min(2, (planList || []).length || 2);
    });
  }

  function styleFrequencySelect(select, options) {
    const collapse = Boolean(options && options.collapse);
    if (collapse) {
      if (select.hasAttribute('size')) select.removeAttribute('size');
      if (select.hasAttribute('multiple')) select.removeAttribute('multiple');
    }
    select.setAttribute('aria-label', 'Subscription frequency');
    select.classList.add('ezeats-frequency-select');
  }

  function frequencyOptionSelected(select, option, frequencyLabel, appstleFrequency) {
    if (!option) return false;
    if (digits(select.value) === digits(option.value)) return true;
    if (select.value === option.value) return true;
    const selectedText = select.options[select.selectedIndex]?.textContent;
    return exactFrequencyMatch(selectedText, appstleFrequency) || exactFrequencyMatch(selectedText, frequencyLabel);
  }

  function isCustomFrequencyControlSelected(control) {
    if (!control) return false;
    if (control.matches('input')) return control.checked;
    if (control.getAttribute('aria-selected') === 'true') return true;
    if (control.getAttribute('aria-checked') === 'true') return true;
    if (control.classList.contains('as-selected') || /\b(selected|active|checked)\b/i.test(control.className || '')) return true;
    return Boolean(control.querySelector('input:checked'));
  }

  function isFrequencyApplied(app, state, plans) {
    const plan = planById(plans, state.sellingPlanId) || planByKey(plans, state.frequencyKey);
    const sellingPlanId = digits((plan && plan.sellingPlanId) || state.sellingPlanId);
    const frequencyLabel = (plan && plan.frequencyLabel) || state.frequencyLabel;
    const appstleFrequency = (plan && plan.appstleFrequency) || state.appstleFrequency || frequencyLabel;
    if (!sellingPlanId && !appstleFrequency) return false;

    const selects = frequencySelects(app, plans);
    if (selects.length) {
      return selects.some(select => {
        const option = findSellingPlanOption(select, sellingPlanId, frequencyLabel, appstleFrequency);
        return frequencyOptionSelected(select, option, frequencyLabel, appstleFrequency);
      });
    }

    const listboxTrigger = app.querySelector('button[aria-haspopup="listbox"]');
    if (listboxTrigger) {
      const openListbox = app.querySelector('[role="listbox"]');
      if (openListbox) {
        const selected = openListbox.querySelector('[aria-selected="true"], [aria-checked="true"]');
        const text = selected ? (selected.textContent || '') : '';
        return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
      }
      const text = listboxTrigger.textContent || '';
      return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
    }

    const listbox = app.querySelector('[role="listbox"]');
    if (listbox) {
      const selected = listbox.querySelector('[aria-selected="true"], [aria-checked="true"]');
      const text = selected ? (selected.textContent || '') : '';
      return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
    }

    const selectedNodes = Array.from(app.querySelectorAll(
      'input[type="radio"]:checked, [aria-checked="true"], .as-selected, [class*="as-selected"]'
    )).filter(node => node.getAttribute('role') !== 'option' || node.getAttribute('aria-selected') === 'true');

    return selectedNodes.some(node => {
      const text = node.textContent || node.value || '';
      return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
    });
  }

  function applyFrequencyToApp(app, state, plans) {
    const plan = planById(plans, state.sellingPlanId) || planByKey(plans, state.frequencyKey);
    const sellingPlanId = digits((plan && plan.sellingPlanId) || state.sellingPlanId);
    const frequencyLabel = (plan && plan.frequencyLabel) || state.frequencyLabel;
    const appstleFrequency = (plan && plan.appstleFrequency) || state.appstleFrequency || frequencyLabel;
    if (!sellingPlanId && !appstleFrequency) return { ok: false, reason: 'missing-plan' };

    const selects = frequencySelects(app, plans);
    selects.forEach(select => styleFrequencySelect(select, { collapse: false }));

    if (isFrequencyApplied(app, state, plans)) {
      if (sellingPlanId) {
        updateState({
          sellingPlanId,
          frequencyLabel: frequencyLabel || state.frequencyLabel,
          appstleFrequency: appstleFrequency || state.appstleFrequency,
          frequencyKey: (plan && plan.frequencyKey) || state.frequencyKey
        });
      }
      return { ok: true, sellingPlanId, frequencyLabel, appstleFrequency };
    }

    let matched = false;
    selects.forEach(select => {
      styleFrequencySelect(select, { collapse: true });
      const option = findSellingPlanOption(select, sellingPlanId, frequencyLabel, appstleFrequency);
      if (!option) return;
      if (frequencyOptionSelected(select, option, frequencyLabel, appstleFrequency)) {
        matched = true;
        return;
      }
      select.value = option.value;
      option.selected = true;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      matched = frequencyOptionSelected(select, option, frequencyLabel, appstleFrequency);
    });

    if (!matched) {
      const listboxTrigger = app.querySelector('button[aria-haspopup="listbox"]');
      if (listboxTrigger) {
        if (listboxTrigger.dataset.ezeatsNoMatch === 'true') {
          // Already confirmed once this session that no option matches this frequency — stop
          // reopening/closing the dropdown every poll tick (that was the cause of the visible
          // blinking). Leave matched=false so the caller's error banner can surface instead.
        } else {
          const currentText = listboxTrigger.textContent || '';
          if (exactFrequencyMatch(currentText, appstleFrequency) || exactFrequencyMatch(currentText, frequencyLabel)) {
            matched = true;
          } else {
            let listbox = app.querySelector('[role="listbox"]');
            const openedHere = !listbox;
            if (openedHere) {
              listboxTrigger.click();
              listbox = app.querySelector('[role="listbox"]');
            }
            const option = listbox ? Array.from(listbox.querySelectorAll('[role="option"]')).find(opt => {
              const text = opt.textContent || '';
              return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
            }) : null;
            if (option) {
              option.click();
              matched = true;
            } else {
              listboxTrigger.dataset.ezeatsNoMatch = 'true';
              if (openedHere && listboxTrigger.getAttribute('aria-expanded') === 'true') listboxTrigger.click();
            }
          }
        }
      } else {
        const controls = Array.from(app.querySelectorAll('label, button, [role="radio"], [role="option"]'));
        const control = controls.find(item => {
          const text = item.textContent || '';
          return exactFrequencyMatch(text, appstleFrequency) || exactFrequencyMatch(text, frequencyLabel);
        });
        if (control) {
          if (isCustomFrequencyControlSelected(control)) {
            matched = true;
          } else {
            const input = control.matches('input') ? control : (control.querySelector('input') || control);
            input.click();
            input.dispatchEvent(new Event('change', { bubbles: true }));
            matched = true;
          }
        }
      }
    }

    if (matched && sellingPlanId) {
      updateState({
        sellingPlanId,
        frequencyLabel: frequencyLabel || state.frequencyLabel,
        appstleFrequency: appstleFrequency || state.appstleFrequency,
        frequencyKey: (plan && plan.frequencyKey) || state.frequencyKey
      });
    }
    return { ok: matched, sellingPlanId, frequencyLabel, appstleFrequency };
  }

  function buildCartAttributesFromState(state, extra) {
    const attributes = Object.assign({}, extra || {});
    attributes['Subscription type'] = state.frequencyLabel || attributes['Subscription type'] || '';
    attributes['Frequency Key'] = state.frequencyKey || attributes['Frequency Key'] || '';
    attributes['Selling Plan ID'] = state.sellingPlanId || attributes['Selling Plan ID'] || '';
    attributes['Requested first order date'] = state.requestedDate || attributes['Requested first order date'] || '';
    attributes['Order Type'] = state.orderType || attributes['Order Type'] || attributes['Requested order type'] || '';
    attributes['Requested order type'] = attributes['Order Type'];
    attributes['Meals per week'] = state.meals ? (state.meals + ' meals') : (attributes['Meals per week'] || '');

    if (attributes['Order Type'] === 'Pickup') {
      attributes['Pickup Location'] = state.pickupLocationName || attributes['Pickup Location'] || '';
      attributes['Pickup Location ID'] = state.pickupLocationId || attributes['Pickup Location ID'] || '';
      attributes['Pickup branch'] = attributes['Pickup Location'];
      attributes['Delivery Address'] = '';
      attributes['Delivery address'] = '';
      attributes['Delivery first name'] = '';
      attributes['Delivery last name'] = '';
      attributes['Delivery address 1'] = '';
      attributes['Delivery address 2'] = '';
      attributes['Delivery city'] = '';
      attributes['Delivery province'] = '';
      attributes['Delivery postal code'] = '';
      attributes['Delivery country'] = '';
    } else if (attributes['Order Type'] === 'Delivery') {
      attributes['Pickup Location'] = '';
      attributes['Pickup Location ID'] = '';
      attributes['Pickup branch'] = '';
      attributes['Delivery Address'] = state.deliveryAddress || attributes['Delivery Address'] || attributes['Delivery address'] || '';
      if (attributes['Delivery Address']) attributes['Delivery address'] = attributes['Delivery Address'];
    } else {
      attributes['Order Type'] = '';
      attributes['Requested order type'] = '';
      attributes['Pickup Location'] = '';
      attributes['Pickup Location ID'] = '';
      attributes['Pickup branch'] = '';
      attributes['Delivery Address'] = '';
      attributes['Delivery address'] = '';
    }
    return attributes;
  }

  // Merge box state over the cart's current attributes. Posting state alone would
  // blank Order Type, pickup/delivery details and the date whenever sessionStorage
  // is empty (new tab, cleared state).
  async function syncCartAttributesFromState(fetchImpl) {
    const request = fetchImpl || window.fetch.bind(window);
    const current = await request(cartRoute('cart.js'), { headers: { Accept: 'application/json' } });
    if (!current.ok) throw new Error('Cart could not be read.');
    const cart = await current.json();
    const response = await request(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: buildCartAttributesFromState(readState(), cart.attributes || {}) })
    });
    if (!response.ok) throw new Error('Cart attributes could not be saved.');
  }

  const modal = document.querySelector('[data-box-modal]');
  const plans = modal ? parsePlanMap(modal) : [];

  if (modal) {
    const form      = modal.querySelector('[data-box-form]');
    const steps     = Array.from(modal.querySelectorAll('[data-box-step]'));
    const titleEl   = modal.querySelector('[data-box-step-title]');
    const backBtn   = modal.querySelector('[data-box-back]');
    const nextBtn   = modal.querySelector('[data-box-next]');
    const finishBtn = modal.querySelector('[data-box-finish]');
    const errorEl   = modal.querySelector('[data-box-error]');
    const dateInput = modal.querySelector('[data-order-date]');
    const dateHelp  = modal.querySelector('[data-date-help]');
    let index = 0;
    let lastFocused = null;

    function refreshDate() {
      if (!dateInput) return;
      try {
        const min = earliestDate(new Date(), modal.dataset.timezone, modal.dataset.cutoff);
        dateInput.min = min;
        if (!dateInput.value) dateInput.value = min;
        dateInput.setCustomValidity(dateInput.value && dateInput.value < min ? 'Please select ' + min + ' or later.' : '');
        if (dateHelp) dateHelp.textContent = '* Earliest requested date: ' + min + '. Final availability is confirmed by the store.';
      } catch {
        dateInput.setCustomValidity('Date configuration is unavailable. Please contact the store.');
      }
    }

    function render() {
      steps.forEach((s, i) => { s.hidden = i !== index; });
      if (titleEl) titleEl.textContent = steps[index].dataset.stepTitle || '';
      if (backBtn) backBtn.hidden = index === 0;
      const last = index === steps.length - 1;
      if (nextBtn) nextBtn.hidden = last;
      finishBtn.hidden = !last;
      errorEl.textContent = '';
      if (index === 0) refreshDate();
    }

    function stepValid() {
      const fields = steps[index].querySelectorAll('input, select');
      for (const f of fields) { if (!f.reportValidity()) return false; }
      return true;
    }

    function collectSelectionState() {
      const frequency = form.querySelector('input[name="attributes[Subscription type]"]:checked');
      const meals = form.querySelector('input[name="attributes[Meals per week]"]:checked');
      if (!meals) throw new Error('Please choose how many meals per order.');
      const previous = readState();
      // Frequency is picked on Appstle's review step, not here. Without a choice in
      // this form, leave it empty so Appstle's own dropdown decides.
      const sellingPlanId = digits(frequency && frequency.dataset.sellingPlan);
      const frequencyKey = (frequency && frequency.dataset.frequencyKey) || '';
      const appstleFrequency = (frequency && (frequency.dataset.appstleFrequency || frequency.value)) || '';
      if (frequency && !sellingPlanId) {
        throw new Error('A valid selling plan could not be resolved for the selected frequency. Please choose another plan or contact the store.');
      }
      if (sellingPlanId && !planById(plans, sellingPlanId) && plans.length) {
        warnDesign('Selected selling plan ID is not in the configured plan map.', sellingPlanId);
      }
      return updateState({
        frequencyKey,
        frequencyLabel: (frequency && frequency.value) || '',
        appstleFrequency,
        sellingPlanId,
        orderType: previous.orderType || '',
        pickupLocationId: previous.pickupLocationId || '',
        pickupLocationName: previous.pickupLocationName || '',
        deliveryAddress: previous.deliveryAddress || '',
        requestedDate: (dateInput && dateInput.value) || previous.requestedDate || '',
        meals: meals.dataset.meals || '',
        planProductId: meals.dataset.productId || '',
        selectedProducts: previous.selectedProducts || {}
      });
    }

    function open(event) {
      lastFocused = document.activeElement;
      const carried = event && event.detail && event.detail.attributes;
      if (carried) {
        Object.entries(carried).forEach(([key, value]) => {
          const fields = form.querySelectorAll('[name="attributes[' + CSS.escape(key) + ']"]');
          fields.forEach(field => {
            if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === value;
            else field.value = value;
          });
        });
        if (carried['Pickup Location'] || carried['Pickup branch']) {
          const pickupSelect = form.querySelector('[data-pickup-select]');
          if (pickupSelect) pickupSelect.value = carried['Pickup Location'] || carried['Pickup branch'];
        }
      }
      const saved = readState();
      if (saved.frequencyKey || saved.sellingPlanId) {
        form.querySelectorAll('input[name="attributes[Subscription type]"]').forEach(input => {
          const sameId = digits(input.dataset.sellingPlan) && digits(input.dataset.sellingPlan) === digits(saved.sellingPlanId);
          const sameKey = normalize(input.dataset.frequencyKey) === normalize(saved.frequencyKey);
          if (sameId || sameKey) input.checked = true;
        });
      }
      if (saved.requestedDate && dateInput) dateInput.value = saved.requestedDate;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      index = 0;
      render();
      (modal.querySelector('input, button') || modal).focus();
    }
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    document.addEventListener('box:open', open);
    nextBtn.addEventListener('click', () => { if (stepValid()) { index = Math.min(index+1, steps.length-1); render(); } });
    backBtn.addEventListener('click', () => { index = Math.max(index-1, 0); render(); });
    modal.querySelectorAll('[data-box-close]').forEach(el => el.addEventListener('click', close));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !modal.hidden) close(); });
    if (dateInput) { dateInput.addEventListener('input', refreshDate); dateInput.addEventListener('focusin', refreshDate); }

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!stepValid()) return;
      finishBtn.disabled = true;
      errorEl.textContent = '';
      try {
        if (await cartHasBox()) {
          const link = document.createElement('a');
          link.href = cartRoute('cart');
          link.textContent = 'Open your cart';
          errorEl.append('You already have a meal box in your cart. ', link, ' to check out or remove it before starting a new one.');
          finishBtn.disabled = false;
          return;
        }
        const state = collectSelectionState();
        const raw = {};
        for (const [key, value] of new FormData(form).entries()) {
          const m = key.match(/^attributes\[(.+)\]$/);
          if (m) raw[m[1]] = value;
        }
        const attributes = buildCartAttributesFromState(state, raw);
        const res = await fetch(cartRoute('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes })
        });
        if (!res.ok) throw new Error('Your preferences could not be saved. Please try again.');

        const chosen = form.querySelector('input[name="attributes[Meals per week]"]:checked');
        let dest = (chosen && chosen.dataset.destination) || modal.dataset.boxUrl || modal.dataset.buildPath;
        const url = storefrontUrl(dest);
        if (!['https:', 'http:'].includes(url.protocol)) throw new Error('The meal builder link is not configured correctly.');
        url.searchParams.set('date', state.requestedDate || '');
        url.searchParams.set('frequency', state.appstleFrequency || state.frequencyLabel || '');
        url.searchParams.set('frequency_key', state.frequencyKey || '');
        url.searchParams.set('selling_plan', state.sellingPlanId || '');
        if (state.meals) url.searchParams.set('meals', state.meals);
        if (state.planProductId) url.searchParams.set('plan_product_id', state.planProductId);
        location.assign(url.href);
      } catch (err) {
        errorEl.textContent = err.message || 'Something went wrong. Please try again.';
        finishBtn.disabled = false;
      }
    });

    // Back button restores this page from the browser's cache exactly as it was
    // left, including the button we disabled before redirecting. Re-enable it.
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      finishBtn.disabled = false;
      errorEl.textContent = '';
    });

    const boxUrl = modal.dataset.boxUrl;
    const buildPath = modal.dataset.buildPath;
    document.addEventListener('click', e => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      const matchesBuild = buildPath && (href === buildPath || href.replace(/\/$/, '') === buildPath.replace(/\/$/, ''));
      let matchesBox = false;
      if (boxUrl && href) {
        try {
          matchesBox = storefrontUrl(href).pathname.replace(/\/$/, '') === storefrontUrl(boxUrl).pathname.replace(/\/$/, '');
        } catch (err) {
          matchesBox = href === boxUrl;
        }
      }
      if (link.hasAttribute('data-open-box-modal') || matchesBuild || matchesBox) {
        e.preventDefault();
        // With items already in the cart, "Order now" means finishing that order:
        // take the shopper to the cart. (On the cart page itself, keep the popup so
        // "Continue choosing" still works.)
        const cartPath = cartRoute('cart').replace(/\/$/, '');
        if (location.pathname.replace(/\/$/, '') === cartPath) {
          open();
          return;
        }
        fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(response => (response.ok ? response.json() : null))
          .then(cart => {
            if (cart && cart.item_count > 0) location.assign(cartRoute('cart'));
            else open();
          })
          .catch(() => open());
      }
    });
  }

  function stateFromUrl(params) {
    const sellingPlanId = digits(params.get('selling_plan'));
    const frequencyKey = params.get('frequency_key') || '';
    const frequency = params.get('frequency') || '';
    const plan = planById(plans, sellingPlanId) || planByKey(plans, frequencyKey) || plans.find(item => exactFrequencyMatch(item.appstleFrequency, frequency) || exactFrequencyMatch(item.frequencyLabel, frequency));
    const hasPlanParams = Boolean(sellingPlanId || frequencyKey || frequency);
    const saved = readState();
    return updateState({
      sellingPlanId: hasPlanParams ? digits((plan && plan.sellingPlanId) || sellingPlanId) : saved.sellingPlanId,
      frequencyKey: hasPlanParams ? ((plan && plan.frequencyKey) || frequencyKey) : saved.frequencyKey,
      frequencyLabel: hasPlanParams ? ((plan && plan.frequencyLabel) || frequency) : saved.frequencyLabel,
      appstleFrequency: hasPlanParams ? ((plan && plan.appstleFrequency) || frequency) : saved.appstleFrequency,
      orderType: params.get('type') || readState().orderType,
      requestedDate: params.get('date') || readState().requestedDate,
      meals: (params.get('meals') || '').replace(/[^0-9]/g, '') || readState().meals,
      planProductId: params.get('plan_product_id') || readState().planProductId,
      pickupLocationId: params.get('pickup_location_id') || readState().pickupLocationId,
      pickupLocationName: params.get('pickup_location') || readState().pickupLocationName
    });
  }

  function readProgress(app) {
    const minimumText = (app.textContent || '').match(/select\s+(\d+)\s+minimum/i);
    const requiredFromState = Number(readState().meals) || (minimumText ? Number(minimumText[1]) : 0);
    const scopedNodes = [
      app.querySelector('.as-progress-bar'),
      app.querySelector('[class*="as-progress"]'),
      app.querySelector('.as-bab-header')
    ].filter(Boolean);

    for (const node of scopedNodes) {
      const text = node.textContent || '';
      const match = text.match(/(\d+)\s*(?:\/|of)\s*(\d+)/i);
      if (match) {
        return {
          selected: Number(match[1]) || 0,
          required: Number(match[2]) || requiredFromState
        };
      }
    }

    let selected = 0;
    app.querySelectorAll('.ezeats-meal-card, .as-product-bab-available, .ezeats-dynamic-card').forEach(card => {
      selected += readCardQty(card);
    });
    return { selected, required: requiredFromState };
  }

  function ensureBottomNav(app) {
    let bar = document.querySelector('[data-ezeats-step-bar]');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'ezeats-step-bar';
      bar.setAttribute('data-ezeats-step-bar', '');
      bar.innerHTML =
        '<div class="ezeats-step-bar__inner">' +
          '<button type="button" class="ezeats-step-bar__btn ezeats-step-bar__btn--ghost" data-ezeats-prev>Previous step</button>' +
          '<p class="ezeats-step-bar__status" data-ezeats-status aria-live="polite"></p>' +
          '<button type="button" class="ezeats-step-bar__btn ezeats-step-bar__btn--primary" data-ezeats-next>Next step</button>' +
        '</div>';
      document.body.appendChild(bar);
      bar.querySelector('[data-ezeats-prev]').addEventListener('click', () => {
        const live = document.querySelector('#root .appstle_bundle') || app;
        const prev = live.querySelector('.as-previous-step, .as-prev-step, button.as-back, [class*="previous"]');
        const candidates = Array.from(live.querySelectorAll('button, a')).filter(el => /previous/i.test(el.textContent || ''));
        const target = prev || candidates[0];
        if (target) target.click();
        else history.back();
      });
      bar.querySelector('[data-ezeats-next]').addEventListener('click', () => {
        const live = document.querySelector('#root .appstle_bundle') || app;
        const nextBtn = bar.querySelector('[data-ezeats-next]');
        const onReview = /review/i.test(live.querySelector('.as-single-product-title')?.textContent || '') || Boolean(live.querySelector('.as-review-bundle, [class*="review-bundle"]'));
        if (onReview) {
          if (nextBtn.dataset.loading === 'true') return;
          const state = ensureSellingPlan(live);
          if (!digits(state.sellingPlanId)) {
            showModal('Your subscription frequency could not be confirmed. Please go back and choose your plan again.');
            return;
          }
          const applied = applyFrequencyToApp(live, state, plans);
          if (!applied.ok) {
            showModal('The selected subscription plan could not be applied. Please reselect your frequency before checkout.');
            return;
          }
          nextBtn.dataset.loading = 'true';
          nextBtn.classList.add('is-loading');
          nextBtn.disabled = true;
          const checkoutBtn = Array.from(live.querySelectorAll('button')).find(btn => /add to cart|checkout|place order|continue/i.test(btn.textContent || ''));
          (checkoutBtn || live.querySelector('.as-next-step'))?.click();
          window.setTimeout(() => {
            nextBtn.dataset.loading = 'false';
            nextBtn.classList.remove('is-loading');
            nextBtn.disabled = false;
          }, 4000);
          return;
        }
        const next = live.querySelector('.as-next-step:not([disabled])');
        
        if (next) next.click();
        else live.querySelector('.as-cart-btn')?.click();
      });
    }
    const progress = readProgress(app);
    const status = bar.querySelector('[data-ezeats-status]');
    const nextBtn = bar.querySelector('[data-ezeats-next]');
    const onProducts = Boolean(app.querySelector('.as-products'));
    const onReview = /review/i.test(app.querySelector('.as-single-product-title')?.textContent || '') || Boolean(app.querySelector('.as-review-bundle, [class*="review-bundle"]'));
    bar.hidden = !(onProducts || onReview);
    document.body.classList.toggle('ezeats-has-step-bar', !bar.hidden);
    document.body.classList.toggle('ezeats-on-products', onProducts && !onReview);
    document.body.classList.toggle('ezeats-on-review', onReview);
    if (status) {
      status.textContent = onReview
        ? 'Review your bundle'
        : (progress.required ? (progress.selected + ' of ' + progress.required + ' meals selected') : (progress.selected + ' meals selected'));
    }
    if (nextBtn && nextBtn.dataset.loading !== 'true') {
      nextBtn.textContent = onReview ? 'Continue to Checkout' : (progress.required && progress.selected >= progress.required ? 'Review Order' : 'Next step');
      nextBtn.disabled = onProducts && progress.required > 0 && progress.selected !== progress.required;
      if (onReview) nextBtn.disabled = false;
    }
  }

  function readCardQty(card) {
    const classQty = (card.querySelector('[class*="quantity-"]')?.className || '').match(/(?:^|\s)quantity-(\d+)(?:\s|$)/);
    if (classQty) return Number(classQty[1]) || 0;
    const controls = findCardControls(card);
    if (controls.add && !controls.plus && !controls.minus) return 0;
    const qtyEl = card.querySelector('[data-ezeats-role="value"]') || card.querySelector('.as-quantity, [class*="as-qty"], [class*="quantity"]');
    if (qtyEl) {
      const raw = qtyEl.matches('input') ? qtyEl.value : qtyEl.textContent;
      const n = Number(String(raw || '').replace(/[^\d]/g, ''));
      if (!Number.isNaN(n)) return n;
    }
    if (controls.section) {
      const hasStepper = Boolean(controls.plus || controls.minus);
      if (hasStepper) {
        const text = (controls.section.textContent || '').replace(/[^\d]+/g, ' ').trim();
        const parts = text.split(/\s+/).map(Number).filter(n => !Number.isNaN(n));
        if (parts.length) return parts[parts.length - 1];
      }
    }
    return 0;
  }

  function findCardControls(card) {
    const section = card.querySelector('.as-card-button-section, [class*="card-button"], .ezeats-qty-native') || card;
    const buttons = Array.from(section.querySelectorAll('button'));
    const add = buttons.find(btn => btn.classList.contains('as-add') || /^add$/i.test((btn.textContent || '').trim()));
    let plus = buttons.find(btn =>
      btn.dataset.ezeatsRole === 'plus' ||
      btn.classList.contains('as-plus') ||
      /^[+＋]$/.test((btn.textContent || '').trim()) ||
      /increase|plus|add one/i.test(btn.getAttribute('aria-label') || '')
    );
    let minus = buttons.find(btn =>
      btn.dataset.ezeatsRole === 'minus' ||
      btn.classList.contains('as-minus') ||
      btn.classList.contains('as-remove') ||
      /^[−\-–]$/.test((btn.textContent || '').trim()) ||
      /decrease|minus|remove/i.test(btn.getAttribute('aria-label') || '')
    );
    if (!minus && buttons.length >= 2) minus = buttons[0];
    if (!plus && buttons.length >= 2) plus = buttons[buttons.length - 1];
    return { section, add, plus, minus, buttons };
  }

  function tagNativeQtyControls(card) {
    const section = card.querySelector('.as-card-button-section, [class*="card-button"]');
    if (!section) return null;
    section.classList.add('ezeats-qty-native');
    section.classList.remove('ezeats-qty-native--proxied');
    // Appstle re-renders by reusing button elements (the lone "Add" becomes the
    // minus of the stepper), so roles from an earlier pass must not stick.
    section.querySelectorAll('button[data-ezeats-role]').forEach(btn => btn.removeAttribute('data-ezeats-role'));
    section.querySelectorAll('button[data-ezeats-aria]').forEach(btn => {
      btn.removeAttribute('aria-label');
      btn.removeAttribute('data-ezeats-aria');
    });
    const { add, plus, minus, buttons } = findCardControls(card);
    if (minus) {
      minus.setAttribute('data-ezeats-role', 'minus');
      if (!minus.getAttribute('aria-label')) {
        const title = (card.querySelector('.as-product-title')?.textContent || 'meal').trim();
        minus.setAttribute('aria-label', 'Remove one ' + title);
        minus.setAttribute('data-ezeats-aria', '');
      }
    }
    if (plus || add) {
      const plusBtn = plus || add;
      plusBtn.setAttribute('data-ezeats-role', 'plus');
      if (!plusBtn.getAttribute('aria-label')) {
        const title = (card.querySelector('.as-product-title')?.textContent || 'meal').trim();
        plusBtn.setAttribute('aria-label', 'Add one ' + title);
        plusBtn.setAttribute('data-ezeats-aria', '');
      }
    }
    buttons.forEach(btn => {
      if (btn.dataset.ezeatsRole) return;
      const label = (btn.textContent || '').trim();
      if (/^[−\-–]$/.test(label)) btn.setAttribute('data-ezeats-role', 'minus');
      else if (/^[+＋]$/.test(label) || /^add$/i.test(label)) btn.setAttribute('data-ezeats-role', 'plus');
    });
    Array.from(section.querySelectorAll('span, div, input, p')).forEach(node => {
      if (node.closest('button')) return;
      const text = (node.textContent || '').trim();
      if (/^\d+$/.test(text) || node.classList.contains('as-quantity') || /qty|quantity/i.test(node.className || '')) {
        node.setAttribute('data-ezeats-role', 'value');
      }
    });
    return section;
  }

  function removeQuantityProxy(card) {
    card.querySelectorAll('[data-ezeats-qty-proxy]').forEach(proxy => proxy.remove());
    card.querySelectorAll('.ezeats-qty-native--proxied').forEach(node => {
      node.classList.remove('ezeats-qty-native--proxied');
    });
  }

  function detectDietBadge(card, title) {
    const existing = card.querySelector('.ezeats-meal-badge, .menu-badge, .as-badge, [class*="badge"]');
    if (existing && !existing.classList.contains('ezeats-meal-badge')) {
      const text = (existing.textContent || '').trim();
      if (/non/i.test(text)) return 'Non Veg';
      if (/^veg$/i.test(text) || /\bveg\b/i.test(text)) return /non/i.test(text) ? 'Non Veg' : 'Veg';
      if (text && text.length < 24) return text;
    }
    const hay = normalize([
      title,
      card.getAttribute('data-tags') || '',
      card.getAttribute('data-product-type') || '',
      card.querySelector('.as-product-description')?.textContent || '',
      card.querySelector('.as-top-tags')?.textContent || ''
    ].join(' '));
    if (/\bnon[\s-]?veg\b|\bnonveg\b/.test(hay)) return 'Non Veg';
    if (/\bveg\b|\bvegetarian\b|\bvegan\b/.test(hay)) return 'Veg';
    return '';
  }

  function isCardSoldOut(card) {
    if (card.classList.contains('as-sold-out') || card.classList.contains('as-unavailable')) return true;
    const native = card.querySelector('.as-card-button-section, [class*="sold"], [class*="unavailable"]');
    if (native && /sold\s*out|out of stock|unavailable/i.test(native.textContent || '')) return true;
    return Boolean(card.querySelector('button[disabled].as-add')) && /sold|stock/i.test(card.querySelector('.as-card-button-section')?.textContent || '');
  }

  const STAR_PATH = 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z';

  function starMarkup(kind) {
    if (kind === 'half') {
      return '<span class="ezeats-star is-half" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#d1d5db"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="' + STAR_PATH + '"></path></svg><span class="ezeats-star__half"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ff6412" stroke="#ff6412"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="' + STAR_PATH + '"></path></svg></span></span>';
    }
    const on = kind === 'full';
    return '<span class="ezeats-star' + (on ? ' is-active' : '') + '" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="' + (on ? '#ff6412' : 'none') + '" stroke="' + (on ? '#ff6412' : '#d1d5db') + '"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="' + STAR_PATH + '"></path></svg></span>';
  }

  function starsForRating(rating) {
    let html = '';
    for (let i = 1; i <= 5; i += 1) {
      if (rating >= i) html += starMarkup('full');
      else if (rating >= i - 0.5) html += starMarkup('half');
      else html += starMarkup('empty');
    }
    return html;
  }

  let mealUrlPromise = null;
  function mealUrlMap() {
    if (!mealUrlPromise) {
      mealUrlPromise = fetch('/collections/meals/products.json?limit=250', { headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : { products: [] })
        .then(data => {
          const map = {};
          (data.products || []).forEach(product => {
            if (product && product.title && product.handle) map[normalize(product.title)] = '/products/' + product.handle;
          });
          return map;
        })
        .catch(() => ({}));
    }
    return mealUrlPromise;
  }

  function openMealDetail(event) {
    const card = event.currentTarget.closest('.ezeats-meal-card');
    const href = card && card.dataset.productUrl;
    if (!href) return;
    if (event.target.closest('button, .ezeats-qty-native, .as-card-button-section, a')) return;
    location.assign(href);
  }

  function bindMealDetailLink(card, title, titleEl, media) {
    const existing = card.querySelector('a[href*="/products/"]');
    if (existing && !card.dataset.productUrl) card.dataset.productUrl = existing.getAttribute('href');
    if (!card.dataset.productUrl) {
      mealUrlMap().then(map => {
        const href = map[normalize(title)];
        if (!href) return;
        card.dataset.productUrl = href;
      });
    }
    if (media && media.dataset.ezeatsDetail !== 'true') {
      media.dataset.ezeatsDetail = 'true';
      media.addEventListener('click', openMealDetail);
    }
    if (titleEl && titleEl.dataset.ezeatsDetail !== 'true') {
      titleEl.dataset.ezeatsDetail = 'true';
      titleEl.addEventListener('click', openMealDetail);
    }
  }

  function unlockMealDecrease(card) {
    card.querySelectorAll('[data-ezeats-role="minus"], .as-minus, .as-remove').forEach(button => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.style.pointerEvents = 'auto';
      button.style.opacity = '1';
    });
  }

  if (!window.__EZEATS_MINUS_UNLOCK__) {
    window.__EZEATS_MINUS_UNLOCK__ = true;
    document.addEventListener('click', event => {
      if (event.__ezeatsMinusReplay) return;
      const card = event.target.closest('.ezeats-meal-card');
      if (!card) return;
      const section = card.querySelector('.ezeats-qty-native, .as-card-button-section');
      if (!section) return;
      const minus = section.querySelector('[data-ezeats-role="minus"], .as-minus, .as-remove');
      if (!minus) return;
      const rect = minus.getBoundingClientRect();
      const hit = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!hit) return;
      if (!minus.disabled && minus.getAttribute('aria-disabled') !== 'true') return;
      event.preventDefault();
      event.stopPropagation();
      minus.disabled = false;
      minus.removeAttribute('aria-disabled');
      const replay = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
      replay.__ezeatsMinusReplay = true;
      minus.dispatchEvent(replay);
    }, true);
  }

  // Appstle's dynamic-pricing builder renders cards with different markup than the
  // fixed-price builder. Tag its parts with the fixed-price class names so the same
  // EZeats card styling and enhancements apply to both.
  function normalizeDynamicCards(app) {
    const dynamicCards = app.querySelectorAll('.as-products [class*="product-id-"]');
    document.body.classList.toggle('ezeats-dynamic-builder', dynamicCards.length > 0);
    dynamicCards.forEach(card => {
      card.classList.add('ezeats-dynamic-card');
      const img = card.querySelector('img');
      const direct = node => node && Array.from(card.children).find(child => child.contains(node));
      direct(img)?.classList.add('as-image-wrapper');
      const title = card.querySelector('h3');
      if (title) {
        title.classList.add('as-product-title');
        const section = direct(title);
        if (section) {
          section.classList.add('as-card-section');
          section.querySelector(':scope > p')?.classList.add('as-product-description');
        }
      }
      const qty = card.querySelector('[class*="quantity-"]');
      if (qty) {
        qty.classList.add('as-card-button-section');
        // Flatten the price/qty wrappers so price and pill become card-level items.
        for (let node = qty.parentElement; node && node !== card; node = node.parentElement) {
          node.classList.add('ezeats-flatten');
        }
      }
      card.querySelector('[class*="final-price"]')?.closest('p')?.classList.add('as-price');
      // Unavailable meals render a lone "Not Available" button instead of the
      // quantity control; show it where the Add pill would be.
      const unavailable = !qty && Array.from(card.querySelectorAll('button')).find(button => /not available|unavailable|sold out/i.test(button.textContent || ''));
      card.classList.toggle('is-unavailable', Boolean(unavailable));
      if (unavailable) {
        const cta = unavailable.closest('.as-modal-cta-wrapper') || unavailable.parentElement;
        cta.classList.add('ezeats-unavailable-cta');
        unavailable.disabled = true;
        unavailable.setAttribute('aria-disabled', 'true');
        for (let node = cta.parentElement; node && node !== card; node = node.parentElement) {
          node.classList.add('ezeats-flatten');
        }
      }
    });
  }

  function enhanceProductCards(app) {
    normalizeDynamicCards(app);
    app.querySelectorAll('.as-product-bab-available, [class*="product-bab"], .ezeats-dynamic-card').forEach((card, index) => {
      const titleEl = card.querySelector('.as-product-title, [class*="product-title"]');
      const title = (titleEl?.textContent || 'meal').trim();
      card.classList.add('ezeats-meal-card');
      card.classList.toggle('is-soldout', isCardSoldOut(card));

      const img = card.querySelector('img');
      const media = card.querySelector('.as-image-wrapper') || (img && img.parentElement);
      if (media && !media.classList.contains('ezeats-meal-media')) {
        media.classList.add('ezeats-meal-media');
        const badgeLabel = detectDietBadge(card, title);
        if (badgeLabel && !media.querySelector('.ezeats-meal-badge')) {
          const badge = document.createElement('span');
          badge.className = 'menu-badge ezeats-meal-badge';
          badge.textContent = badgeLabel;
          media.appendChild(badge);
        }
        card.querySelectorAll('.as-badge, [class*="badge"]:not(.ezeats-meal-badge)').forEach(node => {
          if (!media.contains(node)) node.style.display = 'none';
        });
      }

      const qtySection = tagNativeQtyControls(card);
      removeQuantityProxy(card);
      bindMealDetailLink(card, title, titleEl, media);
      unlockMealDecrease(card);

      if (!card.dataset.ezeatsRating) card.dataset.ezeatsRating = index % 2 === 0 ? '4' : '4.5';
      const rating = Number(card.dataset.ezeatsRating);
      let stars = card.querySelector('.ezeats-meal-stars');
      if (!stars) {
        stars = document.createElement('div');
        stars.className = 'product-card__stars ezeats-meal-stars';
        if (titleEl) titleEl.insertAdjacentElement('afterend', stars);
        else card.appendChild(stars);
      } else if (titleEl && stars.previousElementSibling !== titleEl) {
        titleEl.insertAdjacentElement('afterend', stars);
      }
      if (stars.dataset.rating !== String(rating)) {
        stars.innerHTML = starsForRating(rating);
        stars.dataset.rating = String(rating);
        stars.setAttribute('aria-hidden', 'true');
      }

      if (!card.querySelector('[data-ezeats-oos]')) {
        const oos = document.createElement('p');
        oos.className = 'ezeats-oos';
        oos.setAttribute('data-ezeats-oos', '');
        oos.textContent = 'OUT OF STOCK';
        const media = card.querySelector('.ezeats-meal-media');
        if (media) media.insertAdjacentElement('afterend', oos);
        else if (qtySection) qtySection.insertAdjacentElement('afterend', oos);
        else card.appendChild(oos);
      }

      card.querySelectorAll('span, p, div, button').forEach(node => {
        if (node.closest('.ezeats-meal-stars, .ezeats-qty-native, .as-card-button-section')) return;
        if ((node.textContent || '').trim() === '...' && node.children.length === 0) {
          node.classList.add('ezeats-ellipsis-hide');
          node.setAttribute('aria-hidden', 'true');
        }
      });

      card.dataset.ezeatsCardReady = 'true';
    });

    const progress = readProgress(app);
    const atMax = progress.required > 0 && progress.selected >= progress.required;
    app.querySelectorAll('.ezeats-meal-card').forEach(card => {
      const qty = readCardQty(card);
      card.classList.toggle('is-selected', qty > 0);
      card.classList.toggle('is-soldout', isCardSoldOut(card));
      card.classList.toggle('is-max', atMax);
      if (card.classList.contains('ezeats-dynamic-card')) syncStockLimit(card, qty, atMax);
      if (qty > 0) unlockMealDecrease(card);
    });
  }

  function enhanceReview(app, state) {
    app.querySelectorAll('[data-ezeats-order-summary]').forEach(summary => summary.remove());
    app.querySelector('.as-review-bundle, [class*="review-bundle"], [class*="review-order"]')?.classList.add('ezeats-review-shell');

    Array.from(app.querySelectorAll('button')).forEach(button => {
      if (/add to cart/i.test(button.textContent || '')) {
        button.textContent = 'Continue to Checkout';
        button.classList.add('ezeats-checkout-cta');
      }
    });
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  }

  function trackSelectedProducts(app) {
    if (window.__EZEATS_PRODUCTS_RESTORING__ || !window.__EZEATS_PRODUCTS_RESTORED__) return;
    const selected = {};
    app.querySelectorAll('.ezeats-meal-card, .as-product-bab-available').forEach(card => {
      const title = (card.querySelector('.as-product-title, [class*="product-title"]')?.textContent || '').trim();
      if (!title) return;
      const qty = readCardQty(card);
      if (qty > 0) selected[title] = qty;
    });
    updateState({ selectedProducts: selected });
  }

  function findCardByTitle(app, title) {
    const target = normalize(title);
    return Array.from(app.querySelectorAll('.ezeats-meal-card, .as-product-bab-available')).find(card => {
      const text = (card.querySelector('.as-product-title, [class*="product-title"]')?.textContent || '').trim();
      return normalize(text) === target;
    }) || null;
  }

  function wait(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  async function restoreSelectedProducts(app) {
    if (window.__EZEATS_PRODUCTS_RESTORED__ || window.__EZEATS_PRODUCTS_RESTORING__) return;
    const saved = readState().selectedProducts || {};
    const entries = Object.entries(saved).filter(([, qty]) => Number(qty) > 0);
    const cardsReady = app.querySelectorAll('.ezeats-meal-card, .as-product-bab-available').length > 0;
    if (!cardsReady) return;
    if (!entries.length) {
      window.__EZEATS_PRODUCTS_RESTORED__ = true;
      return;
    }

    const alreadySelected = entries.every(([title, qty]) => {
      const card = findCardByTitle(app, title);
      return card && readCardQty(card) >= Number(qty);
    });
    if (alreadySelected) {
      window.__EZEATS_PRODUCTS_RESTORED__ = true;
      return;
    }

    window.__EZEATS_PRODUCTS_RESTORING__ = true;
    try {
      for (const [title, targetQty] of entries) {
        const want = Number(targetQty) || 0;
        if (want < 1) continue;
        const card = findCardByTitle(app, title);
        if (!card || isCardSoldOut(card)) continue;

        let guard = 0;
        while (readCardQty(card) < want && guard < want + 8) {
          const controls = findCardControls(card);
          const btn = controls.plus || controls.add;
          if (!btn || btn.disabled) break;
          btn.click();
          await wait(140);
          guard += 1;
        }
      }
    } finally {
      window.__EZEATS_PRODUCTS_RESTORING__ = false;
      window.__EZEATS_PRODUCTS_RESTORED__ = true;
      trackSelectedProducts(app);
    }
  }

  function bindFrequencySelectGuard(app, state) {
    frequencySelects(app, plans).forEach(select => {
      styleFrequencySelect(select);
      if (select.dataset.ezeatsFreqGuard === 'true') return;
      select.dataset.ezeatsFreqGuard = 'true';
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        if (!option) return;
        const matched = planById(plans, option.value) ||
          plans.find(plan => exactFrequencyMatch(option.textContent, plan.appstleFrequency) || exactFrequencyMatch(option.textContent, plan.frequencyLabel));
        if (!matched) {
          warnDesign('Frequency select changed to an unmapped option; restoring saved plan.', option.textContent, option.value);
          applyFrequencyToApp(app, readState(), plans);
          return;
        }
        updateState({
          sellingPlanId: digits(matched.sellingPlanId),
          frequencyKey: matched.frequencyKey,
          frequencyLabel: matched.frequencyLabel,
          appstleFrequency: matched.appstleFrequency
        });
        syncCartAttributesFromState().catch(error => warnDesign('Could not sync frequency cart attributes.', error));
      });
    });
    if (!isFrequencyApplied(app, state, plans)) {
      applyFrequencyToApp(app, state, plans);
    }
  }

  function sellingPlanInBody(body) {
    try {
      if (typeof body === 'string') {
        try {
          const payload = JSON.parse(body);
          const items = Array.isArray(payload.items) ? payload.items : [payload];
          const withPlan = items.find(item => item && (item.selling_plan || item.sellingPlan));
          return withPlan ? digits(withPlan.selling_plan || withPlan.sellingPlan) : '';
        } catch (_) {
          const params = new URLSearchParams(body);
          return digits(params.get('selling_plan') || params.get('items[0][selling_plan]') || '');
        }
      }
      if (body instanceof FormData) return digits(body.get('selling_plan') || body.get('items[0][selling_plan]') || '');
    } catch (error) {
      warnDesign('Could not read selling plan from cart request.', error);
    }
    return '';
  }

  function interceptCartAdds(state) {
    if (window.__EZEATS_FETCH_PATCHED__) return;
    window.__EZEATS_FETCH_PATCHED__ = true;
    if (digits(state && state.sellingPlanId)) markFrequencyChosen();
    const originalFetch = window.fetch.bind(window);
    window.fetch = async function(input, init) {
      const url = String(typeof input === 'string' ? input : (input && input.url) || '');
      const isCartAdd = /\/cart\/add(\.js)?/i.test(url);
      if (!isCartAdd) return originalFetch(input, init);

      if (!frequencyChosen) {
        showModal('Please choose your subscription frequency before adding the box to your cart.');
        return new Response(JSON.stringify({ message: 'Frequency not chosen' }), { status: 400, headers: { 'Content-Type': 'application/json' } });
      }

      // Priority: the plan Appstle put in the request (it reflects the dropdown the
      // shopper sees), then the dropdown itself, then the saved choice.
      const requestPlanId = sellingPlanInBody(init && init.body);
      if (requestPlanId) {
        rememberPlan(planById(plans, requestPlanId) || { sellingPlanId: requestPlanId });
        const response = await originalFetch(input, init);
        if (response.ok) syncCartAttributesFromState(originalFetch).catch(error => warnDesign('Could not refresh cart attributes after add.', error));
        return response;
      }
      if (isOneTimeSelected()) {
        updateState({ sellingPlanId: '', frequencyKey: '', frequencyLabel: '', appstleFrequency: '' });
        return originalFetch(input, init);
      }
      rememberPlan(planFromVisibleFrequency(document.querySelector('#root .appstle_bundle')));
      const sellingPlanId = digits(readState().sellingPlanId);
      if (!sellingPlanId) {
        showModal('Please choose your subscription frequency before adding the box to your cart.');
        return new Response(JSON.stringify({ message: 'Missing selling plan' }), { status: 400, headers: { 'Content-Type': 'application/json' } });
      }

      try {
        const body = init && init.body;
        if (typeof body === 'string') {
          try {
            const payload = JSON.parse(body);
            let changed = false;
            const applyPlan = item => {
              if (!item || typeof item !== 'object') return item;
              const next = Object.assign({}, item);
              if (next.selling_plan != null || next.sellingPlan != null || next.id) {
                next.selling_plan = Number(sellingPlanId);
                changed = true;
              }
              return next;
            };
            if (Array.isArray(payload.items)) {
              payload.items = payload.items.map(applyPlan);
            } else if (payload && payload.id) {
              payload.selling_plan = Number(sellingPlanId);
              changed = true;
            }
            if (changed) {
              init = Object.assign({}, init || {}, {
                body: JSON.stringify(payload),
                headers: Object.assign({}, (init && init.headers) || {}, {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                })
              });
            }
          } catch (_) {
            const params = new URLSearchParams(body);
            if (params.has('id') || params.has('selling_plan')) {
              params.set('selling_plan', sellingPlanId);
              init = Object.assign({}, init || {}, { body: params.toString() });
            }
          }
        } else if (body instanceof FormData) {
          if (body.has('id') || body.has('selling_plan') || body.has('items')) {
            body.set('selling_plan', sellingPlanId);
          }
        }
      } catch (error) {
        warnDesign('Could not enforce selling plan on cart add.', error);
      }

      const response = await originalFetch(input, init);
      if (response.ok) {
        syncCartAttributesFromState(originalFetch).catch(error => warnDesign('Could not refresh cart attributes after add.', error));
      }
      return response;
    };
  }

  // Appstle's dynamic builder opens its cart drawer after every Add. Keep it closed
  // while shoppers pick meals; it still opens from Cart / Review Order.
  let suppressCartTimer = null;
  document.addEventListener('click', event => {
    if (event.target.closest('#root .ezeats-dynamic-card .as-card-button-section button')) {
      document.body.classList.add('ezeats-suppress-cart');
      window.clearTimeout(suppressCartTimer);
      suppressCartTimer = window.setTimeout(() => document.body.classList.remove('ezeats-suppress-cart'), 2500);
    } else if (event.target.closest('.as-cart-btn, [data-ezeats-next]')) {
      window.clearTimeout(suppressCartTimer);
      document.body.classList.remove('ezeats-suppress-cart');
    }
  }, true);

  // A box must hold exactly its meal count (6/12/18/24). Appstle only enforces the
  // minimum, so block checkout from its drawer when the box is over or under.
  document.addEventListener('click', event => {
    const proceed = event.target.closest('.as-bundle-cart-dailog .as-proceed-to-checkout-btn');
    if (!proceed || !document.body.classList.contains('ezeats-dynamic-builder')) return;
    const app = document.querySelector('#root .appstle_bundle');
    if (!app) return;
    const progress = readProgress(app);
    const required = progress.required;
    // After "Next Step" Appstle unmounts the meal grid, so the grid count reads 0.
    // The drawer's own lines are the source of truth for what will be checked out.
    const dialog = proceed.closest('.as-bundle-cart-dailog');
    let drawerCount = 0;
    dialog.querySelectorAll('.as-product-item .as-quantity').forEach(node => {
      drawerCount += parseInt(node.textContent, 10) || 0;
    });
    const selected = drawerCount || progress.selected;
    if (!required || selected === required) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showModal(selected > required
      ? 'Your box holds ' + required + ' meals. Please remove ' + (selected - required) + ' before checkout.'
      : 'Please choose ' + (required - selected) + ' more meal' + (required - selected === 1 ? '' : 's') + ' to complete your box of ' + required + '.');
  }, true);

  // At a meal's stock limit Appstle just disables "+" with no message. Show one
  // when that happens (and again on a tap of the faded "+").
  function showStockNote(card, qty) {
    let note = card.querySelector('.ezeats-stock-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'ezeats-stock-note';
      note.setAttribute('role', 'status');
      card.appendChild(note);
    }
    note.textContent = qty > 0 ? 'Only ' + qty + ' available' : 'No more available';
    const pill = card.querySelector('.as-card-button-section');
    if (pill) note.style.top = (pill.offsetTop - 40) + 'px';
    note.classList.add('is-visible');
    window.clearTimeout(note._ezeatsTimer);
    note._ezeatsTimer = window.setTimeout(() => note.classList.remove('is-visible'), 4000);
    const status = document.getElementById('ThemeStatus');
    if (status) status.textContent = note.textContent + ': ' + (card.querySelector('h3')?.textContent || 'this meal').trim();
  }

  // The disabled "+" has pointer-events off (CSS), so taps land on the pill instead.
  document.addEventListener('click', event => {
    // Appstle may have just re-rendered the pill (dropping our classes), so match on
    // the card and hit-test the "+" position instead.
    const card = event.target.closest('#root .ezeats-dynamic-card.is-stock-max');
    if (!card) return;
    const plus = card.querySelector('[data-action="increment"]');
    if (!plus) return;
    const rect = plus.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
    showStockNote(card, readCardQty(card));
  });

  // Appstle also disables "+" for a moment while each add is saving, so only treat
  // it as a stock limit once it has stayed disabled for 600ms.
  function syncStockLimit(card, qty, atMax) {
    const plus = card.querySelector('[data-action="increment"]') || card.querySelector('[data-ezeats-role="plus"]');
    const candidate = Boolean(plus && plus.disabled && qty > 0 && !atMax);
    if (!candidate) {
      delete card.dataset.ezeatsCapSince;
      card.classList.remove('is-stock-max');
      // Appstle re-enables "+" for a moment on re-render; only hide the note
      // early when the quantity actually dropped below the limit.
      if (qty < Number(card.dataset.ezeatsCapQty || 0)) {
        card.querySelector('.ezeats-stock-note')?.classList.remove('is-visible');
        delete card.dataset.ezeatsCapQty;
      }
      return;
    }
    if (card.classList.contains('is-stock-max')) return;
    if (!card.dataset.ezeatsCapSince) {
      card.dataset.ezeatsCapSince = String(Date.now());
      window.setTimeout(() => {
        const app = document.querySelector('#root .appstle_bundle');
        const progress = app ? readProgress(app) : { selected: 0, required: 0 };
        syncStockLimit(card, readCardQty(card), progress.required > 0 && progress.selected >= progress.required);
      }, 650);
      return;
    }
    if (Date.now() - Number(card.dataset.ezeatsCapSince) < 600) return;
    card.classList.add('is-stock-max');
    const recentlyShown = card.dataset.ezeatsCapQty === String(qty) && Date.now() - Number(card.dataset.ezeatsNoteAt || 0) < 5000;
    if (!recentlyShown) {
      card.dataset.ezeatsCapQty = String(qty);
      card.dataset.ezeatsNoteAt = String(Date.now());
      showStockNote(card, qty);
    }
  }

  function closeSuppressedCart() {
    if (!document.body.classList.contains('ezeats-suppress-cart')) return;
    const dialog = document.querySelector('.as-bundle-cart-dailog');
    const close = dialog && dialog.querySelector('.as-close-btn');
    if (!close) return;
    const last = Number(dialog.dataset.ezeatsAutoClosedAt) || 0;
    if (Date.now() - last < 400) return;
    dialog.dataset.ezeatsAutoClosedAt = String(Date.now());
    close.click();
  }

  // One meal box per cart. Appstle's builder does not look at the cart, so a second
  // box would merge into the first and break the 6/12/18/24 total.
  const isBoxLine = item => Object.keys((item && item.properties) || {}).some(key => /appstle-bb/i.test(key));

  async function readBoxLines() {
    try {
      const response = await fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return [];
      const cart = await response.json();
      return (cart.items || []).filter(isBoxLine);
    } catch (_) {
      return [];
    }
  }

  const cartHasBox = async () => (await readBoxLines()).length > 0;

  function buildBoxBlockNotice() {
    const notice = document.createElement('section');
    notice.className = 'ezeats-box-block';
    notice.setAttribute('data-ezeats-box-block', '');
    notice.innerHTML =
      '<h1>You already have a meal box in your cart</h1>' +
      '<p>To keep every box complete, you can have one box at a time. Check out with the box in your cart, or remove it to build a different one.</p>' +
      '<div class="ezeats-box-block__actions">' +
        '<a class="button" data-box-block-cart>View cart</a>' +
        '<button type="button" class="button button--outline" data-box-block-reset>Remove box and start over</button>' +
      '</div>' +
      '<p class="small ezeats-box-block__error" data-box-block-error role="alert"></p>';
    notice.querySelector('[data-box-block-cart]').href = cartRoute('cart');
    notice.querySelector('[data-box-block-reset]').addEventListener('click', async event => {
      const button = event.currentTarget;
      const error = notice.querySelector('[data-box-block-error]');
      button.disabled = true;
      error.textContent = '';
      try {
        const updates = {};
        (await readBoxLines()).forEach(line => { updates[line.key] = 0; });
        const response = await fetch(cartRoute('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error('Could not remove the box.');
        try { sessionStorage.removeItem(STATE_KEY); } catch (_) {}
        location.reload();
      } catch (_) {
        error.textContent = 'We could not remove the box. Please open your cart and remove it there.';
        button.disabled = false;
      }
    });
    return notice;
  }

  function guardExistingBox() {
    if (!location.pathname.includes('/apps/subscriptions/bb/')) return;
    const apply = async () => {
      const has = await cartHasBox();
      const existing = document.querySelector('[data-ezeats-box-block]');
      document.body.classList.toggle('ezeats-box-in-cart', has);
      if (has && !existing) {
        const main = document.getElementById('MainContent') || document.querySelector('main');
        if (main) main.prepend(buildBoxBlockNotice());
      } else if (!has && existing) {
        existing.remove();
      }
    };
    apply();
    // Coming back with the Back button restores the old page from cache.
    window.addEventListener('pageshow', event => { if (event.persisted) apply(); });
  }

  function watchAppstleCards() {
    if (!location.pathname.includes('/apps/subscriptions/bb/')) return;
    const root = document.getElementById('root');
    if (!root) return;
    document.body.classList.add('ezeats-appstle-page');
    let queued = false;
    const run = () => {
      queued = false;
      const app = document.querySelector('#root .appstle_bundle');
      if (!app || !app.querySelector('.as-products [class*="product-id-"]')) return;
      enhanceProductCards(app);
      ensureBottomNav(app);
    };
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    // The cart drawer is portalled outside #root.
    let cartQueued = false;
    new MutationObserver(() => {
      if (cartQueued || !document.body.classList.contains('ezeats-suppress-cart')) return;
      cartQueued = true;
      requestAnimationFrame(() => { cartQueued = false; closeSuppressedCart(); });
    }).observe(document.body, { childList: true, subtree: true });
    run();
  }

  function continueAppstleHandoff() {
    if (!location.pathname.includes('/apps/subscriptions/bb/')) return;
    document.body.classList.add('ezeats-appstle-page');
    const params = new URLSearchParams(location.search);
    const state = stateFromUrl(params);
    if (!state.meals && !readState().frequencyKey && !readState().sellingPlanId && !readState().appstleFrequency) return;

    if (!digits(state.sellingPlanId)) {
      warnDesign('Handoff started without a resolvable selling plan ID. Refusing silent Weekly fallback.');
    }

    interceptCartAdds(state);
    document.body.classList.add('appstle-handoff-loading');
    const loader = document.createElement('div');
    loader.className = 'appstle-handoff-loader';
    loader.innerHTML = '<span></span><strong>Preparing your ' + (state.meals || '') + ' meal box…</strong>';
    document.querySelector('main')?.prepend(loader);

    let advanced = false;
    let frequencySelected = false;
    let frequencyErrorShown = false;
    let selectClickedAt = 0;
    let nextClickedAt = 0;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const app = document.querySelector('#root .appstle_bundle');
      if (!app) {
        if (Date.now() - started > 15000) {
          loader.remove();
          document.body.classList.remove('appstle-handoff-loading');
          window.clearInterval(timer);
        }
        return;
      }

      const onProducts = Boolean(app.querySelector('.as-products')) || /choose products/i.test(app.querySelector('.as-single-product-title')?.textContent || '');
      const onReview = /review/i.test(app.querySelector('.as-single-product-title')?.textContent || '') || Boolean(app.querySelector('.as-review-bundle, [class*="review-bundle"]'));
      document.body.classList.toggle('ezeats-on-products', onProducts && !onReview);
      document.body.classList.toggle('ezeats-on-review', onReview);
      if (onProducts || onReview) {
        advanced = true;
        loader.remove();
        document.body.classList.remove('appstle-handoff-loading');
      }

      if (state.meals && !advanced) {
        const cards = Array.from(app.querySelectorAll('.as-bab-product'));
        const card = cards.find(item => {
          const title = item.querySelector('.as-display-text')?.textContent || '';
          return new RegExp('^\\s*' + state.meals + '\\s+Meal\\s+Plan\\s*$', 'i').test(title);
        });
        if (card) {
          const selected = /selected/i.test(card.querySelector('button')?.textContent || '') ||
            Boolean(card.querySelector('.as-selected-single-product'));
          if (!selected) {
            const select = Array.from(card.querySelectorAll('button')).find(button => /select plan/i.test(button.textContent || ''));
            if (select && Date.now() - selectClickedAt > 1000) {
              selectClickedAt = Date.now();
              select.click();
            }
          } else {
            const next = app.querySelector('.as-next-step:not([disabled])');
            if (next && Date.now() - selectClickedAt > 500 && Date.now() - nextClickedAt > 1500) {
              nextClickedAt = Date.now();
              next.click();
            }
          }
        }
      }

      if ((readState().sellingPlanId || readState().appstleFrequency) && !frequencySelected) {
        const latest = readState();
        if (isFrequencyApplied(app, latest, plans)) {
          frequencySelected = true;
        } else {
          const result = applyFrequencyToApp(app, latest, plans);
          if (result.ok) frequencySelected = true;
        }
      }

      if (onProducts && !onReview) {
        enhanceProductCards(app);
        restoreSelectedProducts(app);
        trackSelectedProducts(app);
        ensureBottomNav(app);
      }
      if (onReview) {
        const latest = readState();
        if (!isFrequencyApplied(app, latest, plans)) {
          const result = applyFrequencyToApp(app, latest, plans);
          if (!result.ok && !frequencyErrorShown && digits(latest.sellingPlanId)) {
            frequencyErrorShown = true;
            const banner = document.createElement('div');
            banner.className = 'ezeats-plan-error';
            banner.setAttribute('role', 'alert');
            banner.textContent = 'We could not confirm your selected subscription frequency. Please reselect it below before checkout. We will not default to Weekly.';
            app.prepend(banner);
          }
          if (result.ok) frequencySelected = true;
        } else {
          frequencySelected = true;
        }
        bindFrequencySelectGuard(app, latest);
        enhanceReview(app, latest);
        ensureBottomNav(app);
      }

      if ((advanced && frequencySelected) || Date.now() - started > 25000) {
        if (!frequencySelected && digits(readState().sellingPlanId)) {
          warnDesign('Frequency was not confirmed in Appstle UI within the handoff window.');
        }
        loader.remove();
        document.body.classList.remove('appstle-handoff-loading');
        if (advanced) {
          // Keep a slower watcher for review/product UI updates without heavy polling forever.
          window.clearInterval(timer);
          const slow = window.setInterval(() => {
            const live = document.querySelector('#root .appstle_bundle');
            if (!live) return;
            const latest = readState();
            const liveOnReview = /review/i.test(live.querySelector('.as-single-product-title')?.textContent || '') || Boolean(live.querySelector('.as-review-bundle, [class*="review-bundle"]'));
            document.body.classList.toggle('ezeats-on-products', Boolean(live.querySelector('.as-products')) && !liveOnReview);
            document.body.classList.toggle('ezeats-on-review', liveOnReview);
            if (live.querySelector('.as-products') && !liveOnReview) {
              enhanceProductCards(live);
              restoreSelectedProducts(live);
              trackSelectedProducts(live);
              ensureBottomNav(live);
              if (!isFrequencyApplied(live, latest, plans)) {
                applyFrequencyToApp(live, latest, plans);
              }
            }
            if (liveOnReview) {
              if (!isFrequencyApplied(live, latest, plans)) {
                const result = applyFrequencyToApp(live, latest, plans);
                if (!result.ok && !frequencyErrorShown && digits(latest.sellingPlanId)) {
                  frequencyErrorShown = true;
                  const banner = document.createElement('div');
                  banner.className = 'ezeats-plan-error';
                  banner.setAttribute('role', 'alert');
                  banner.textContent = 'We could not confirm your selected subscription frequency. Please reselect it below before checkout. We will not default to Weekly.';
                  live.prepend(banner);
                }
              }
              bindFrequencySelectGuard(live, latest);
              enhanceReview(live, latest);
              ensureBottomNav(live);
            }
          }, 1000);
          window.setTimeout(() => window.clearInterval(slow), 120000);
        } else {
          window.clearInterval(timer);
        }
      }
    }, 400);
  }

  function autoSelectAppstleShippingDate() {
    const requested = document.body.dataset.ezeatsRequestedDate;
    if (!requested) return;
    const target = new Date(requested + 'T00:00:00');
    if (isNaN(target.getTime())) return;
    const targetMonthLabel = target.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const targetDay = target.getDate();

    let tries = 0;
    const waitForWidget = window.setInterval(() => {
      tries++;
      const trigger = document.querySelector('.ap-date-field-button');
      if (!trigger) {
        if (tries > 40) window.clearInterval(waitForWidget);
        return;
      }
      window.clearInterval(waitForWidget);
      if (trigger.dataset.ezeatsDateSynced === 'true') return;
      trigger.dataset.ezeatsDateSynced = 'true';

      const currentLabel = (trigger.textContent || '').trim();
      if (currentLabel && !/choose a date/i.test(currentLabel)) return;

      trigger.click();
      let navTries = 0;
      const navigate = window.setInterval(() => {
        navTries++;
        const calendar = document.querySelector('.react-datepicker');
        if (!calendar) { if (navTries > 20) window.clearInterval(navigate); return; }
        const headerEl = calendar.querySelector('.react-datepicker__current-month');
        const shownLabel = headerEl ? headerEl.textContent.trim() : '';
        if (shownLabel === targetMonthLabel) {
          window.clearInterval(navigate);
          const dayBtn = Array.from(calendar.querySelectorAll('.react-datepicker__day')).find(d =>
            d.textContent.trim() === String(targetDay) &&
            !d.classList.contains('react-datepicker__day--outside-month') &&
            !d.classList.contains('react-datepicker__day--disabled')
          );
          if (dayBtn) dayBtn.click();
          else trigger.click();
          return;
        }
        if (navTries > 20) { window.clearInterval(navigate); trigger.click(); return; }
        const shownDate = shownLabel ? new Date(shownLabel.replace(/^(\w+) (\d+)$/, '$1 1, $2')) : null;
        const goForward = !shownDate || isNaN(shownDate.getTime()) || shownDate < target;
        const navBtn = calendar.querySelector(goForward ? '.react-datepicker__navigation--next' : '.react-datepicker__navigation--previous');
        if (navBtn) navBtn.click();
        else { window.clearInterval(navigate); trigger.click(); }
      }, 150);
    }, 250);
  }

  continueAppstleHandoff();
  watchAppstleCards();
  guardExistingBox();
  autoSelectAppstleShippingDate();
})();
